/**
 * Modèles FastSell premium (électronique, mode, tunnel mono-produit, déco type Pinterest, dropshipping, cosmétiques, accueil éditorial épuré).
 * Fusionnés dans storeTemplates — validés avec validateAllTemplates en développement.
 */
import type { LayoutBlock, LayoutConfig } from '@/storefront-runtime/types'

type ProStoreTemplate = {
  id: string
  name: string
  description: string
  niche: string
  thumbnail: string
  layout: LayoutConfig
}

export const proStoreTemplates: ProStoreTemplate[] = [
  {
    id: 'premium-electronics-v1',
    name: 'Électronique — précision et preuves',
    description: 'Vitrine tech à forte conversion : comparatif, aperçu rapide, filtres, parcours axé confiance.',
    niche: 'Électronique',
    thumbnail: '/templates/premium-electronics-v1/hero-800.webp',
    layout: {
      theme: {
        id: 'dawn',
        primary: 'hsl(217 91% 48%)',
        secondary: 'hsl(214 32% 92%)',
        backgroundColor: '#f8fafc',
        font: 'Inter',
        radius: '0.5rem',
        ui: {
          productCard: 'minimal',
          buttons: 'sharp',
          spacing: 'compact',
          headingStyle: 'clean',
          density: 'tight',
          font: 'sans',
          mode: 'light',
          radius: 'none',
        },
      } as any,
      header: {
        props: {
          storeName: null,
          menuItems: [
            { label: 'Accueil', link: '/' },
            { label: 'Boutique', link: '/shop' },
            { label: 'Smartphones', link: '/category/smartphones' },
            { label: 'Questions fréquentes', link: '/faq' },
            { label: 'Nous contacter', link: '/contact' },
          ],
          showSearch: true,
          showCart: true,
          sticky: true,
          variant: 'default',
        },
      },
      footer: {
        props: {
          storeName: true,
          description: 'Produits certifiés, livraison suivie, garantie locale.',
          copyright: `© ${new Date().getFullYear()} Boutique`,
          columns: [
            { title: 'Boutique', links: [{ label: 'Tous les produits', link: '/shop' }, { label: 'Questions fréquentes', link: '/faq' }] },
            { title: 'Assistance', links: [{ label: 'Livraison', link: '/policy/shipping' }, { label: 'Retours', link: '/policy/returns' }] },
          ],
          socialLinks: { instagram: 'https://instagram.com', facebook: 'https://facebook.com' },
          showPaymentIcons: true,
        },
      },
      pages: {
        home: [
          { id: 'elx-h-announce', type: 'announcement-bar', visible: true, props: { text: 'Stock certifié · Livraison suivie · Options de garantie au paiement', link: '/shop', backgroundColor: '#0f172a', textColor: '#f8fafc' } },
          { id: 'elx-h-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'ShieldCheck', title: 'Stock certifié', text: 'Inventaire tracé par numéro de série.' }, { icon: 'Truck', title: 'Livraison suivie', text: 'SMS dès la création de l’étiquette.' }, { icon: 'Zap', title: 'Support réactif', text: 'Conseillers pour installation, retours, garantie.' }] } },
          { id: 'elx-h-hero', type: 'hero', visible: true, props: { headline: 'Des performances mesurables', subtitle: 'Smartphones, ordinateurs, maison connectée — fiches vérifiées et garantie.', ctaLabel: 'Parcourir le catalogue', anchorId: 'elx-catalog', backgroundImage: '/templates/premium-electronics-v1/hero.png', layout: 'split', overlayOpacity: 62, textColor: '#ffffff' } },
          { id: 'elx-h-qv', type: 'quick-view', visible: true, props: { enabled: true } },
          { id: 'elx-h-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'elx-h-promo', type: 'promo-banner', visible: true, props: { text: 'Mise à niveau flash — réassort prioritaire smartphones et audio', highlight: 'Fin dimanche', backgroundColor: '#2563eb', textColor: '#ffffff', ctaLabel: 'Voir les offres', ctaLink: '/shop', showIcon: true } },
          { id: 'elx-h-spacer-1', type: 'spacer', visible: true, props: { size: 'md' } },
          { id: 'elx-h-shop-img', type: 'shoppable-image', visible: true, props: { image: '/templates/premium-electronics-v1/shoppable.png', hotspots: [{ x: 35, y: 72, productId: 'epx-prod-1' }, { x: 52, y: 68, productId: 'epx-prod-4' }, { x: 78, y: 68, productId: 'epx-prod-7' }] } },
          { id: 'elx-h-featured', type: 'featured-collection', visible: true, props: { title: 'Sélection — smartphones et ordinateurs', subtitle: 'Tests thermiques et batteries vérifiés.', collectionId: '', limit: 8, columns: 4, viewAllLabel: 'Toute l’électronique', viewAllLink: '/shop' } },
          { id: 'elx-h-carousel', type: 'product-carousel', visible: true, props: { title: 'Nouveautés — testées cette semaine', subtitle: 'Smartphones, montres, audio.', limit: 12, autoplay: true, showArrows: true } },
          { id: 'elx-h-compare-home', type: 'comparison-table', visible: true, props: { title: 'Comparer les modèles phares', features: ['Garantie officielle', 'Boîte scellée', 'Charge rapide', 'Résistance à l’eau', 'Reprise'], products: ['epx-prod-1', 'epx-prod-2', 'epx-prod-3'] } },
          { id: 'elx-h-image-text', type: 'image-with-text', visible: true, props: { image: '/templates/premium-electronics-v1/elx-laboratoire-section.png', imageAlt: 'Contrôle d’un ordinateur portable', title: 'Contrôle en laboratoire', text: 'Nous testons les thermiques sous charge et photographions l’état avant expédition.', ctaLabel: 'Nos critères', ctaLink: '/faq', layout: 'image-right', imageAspect: 'landscape' } },
          { id: 'elx-h-bundle', type: 'bundle-offer', visible: true, props: { title: 'Pack démarrage smartphone', productIds: ['epx-prod-1', 'epx-prod-2', 'epx-prod-3'], discountLabel: '−15 % sur téléphone + coque + chargeur' } },
          { id: 'elx-h-faq', type: 'faq-accordion', visible: true, props: { title: 'Questions fréquentes — électronique', subtitle: 'Garantie et livraison', items: [{ question: 'Les produits sont-ils neufs ?', answer: 'Oui, scellés sauf mention « emballage ouvert ».' }, { question: 'Comment comparer les modèles ?', answer: 'Utilisez le tableau ci-dessus ou la matrice sur la fiche produit.' }] } },
          { id: 'elx-h-newsletter', type: 'newsletter', visible: true, props: { title: 'Alertes réassort', subtitle: 'Cartes graphiques et téléphones.', placeholder: 'E-mail', buttonText: 'S’abonner', backgroundColor: '#0f172a', backgroundImage: '', overlayOpacity: 40 } },
        ],
        shop: [
          { id: 'elx-s-announce', type: 'announcement-bar', visible: true, props: { text: 'Filtres synchronisés avec la grille · Aperçu rapide sur les fiches', link: '', backgroundColor: '#1e293b', textColor: '#e2e8f0' } },
          { id: 'elx-s-qv', type: 'quick-view', visible: true, props: { enabled: true } },
          { id: 'elx-s-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'Lock', title: 'Paiement sécurisé', text: 'Tunnel chiffré TLS.' }, { icon: 'Truck', title: 'Délai d’expédition', text: 'En stock : expédition sous 24 h.' }, { icon: 'RefreshCw', title: 'Retours simples', text: '7 jours si produit scellé.' }] } },
          { id: 'elx-s-filters', type: 'filters-sidebar', visible: true, props: { enablePrice: true, enableCategory: true, enableRating: true } },
          { id: 'elx-s-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'elx-s-banner', type: 'image-banner', visible: true, props: { image: '/templates/premium-electronics-v1/vis-banner.svg', title: 'Parcourir par catégorie', subtitle: 'Smartphones · Ordinateurs · Audio', ctaLabel: 'Voir le catalogue', ctaLink: '#elx-catalog', overlayOpacity: 55, minHeight: 'md', textAlign: 'left' } },
          { id: 'elx-s-featured', type: 'featured-collection', visible: true, props: { title: 'Meilleures ventes smartphones', subtitle: 'Références les plus choisies.', collectionId: '', limit: 8, columns: 4, viewAllLabel: 'Tout voir', viewAllLink: '/shop' } },
          { id: 'elx-s-carousel', type: 'product-carousel', visible: true, props: { title: 'Tendances audio et montres', subtitle: 'À combiner avec les téléphones.', limit: 14, autoplay: true, showArrows: true } },
          { id: 'elx-s-grid', type: 'product-grid', visible: true, props: { title: 'Catalogue complet', subtitle: 'Filtrez puis ouvrez l’aperçu rapide.', limit: 36, columns: 4, anchorId: 'elx-catalog' } },
          { id: 'elx-s-newsletter', type: 'newsletter', visible: true, props: { title: 'Alertes baisse de prix', subtitle: 'Une notification quand le prix baisse.', placeholder: 'E-mail', buttonText: 'S’abonner', backgroundColor: '#1e3a8a', backgroundImage: '', overlayOpacity: 35 } },
        ],
        product: [
          { id: 'elx-p-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'ShieldCheck', title: 'Garantie', text: 'Détail au paiement.' }, { icon: 'Package', title: 'Numéro de série', text: 'Photos sur les produits à forte valeur.' }, { icon: 'Zap', title: 'Aide à l’installation', text: 'WhatsApp pour le couplage.' }] } },
          { id: 'elx-p-details', type: 'product-details', visible: true, props: { highlights: ['Thermiques testés', 'Accessoires d’origine', 'Options de garantie'], trustShipping: 'Livraison suivie sur tout le territoire.', trustReturns: 'Retours 7 jours si scellé.', trustPayment: 'Paiement à la livraison + cartes — total figé avant débit.' } },
          { id: 'elx-p-tabs', type: 'product-tabs', visible: true, props: { tabs: [{ label: 'Fiche technique', content: 'Ports, puissance, références régionales.' }, { label: 'Dans la boîte', content: 'Carte série, chargeur, guides.' }, { label: 'Garantie', content: 'Procédure SAV et partenaires.' }] } },
          { id: 'elx-p-compare', type: 'comparison-table', visible: true, props: { title: 'Matrice technique', features: ['Garantie', 'Écran', 'Charge rapide', 'Indice IP', '5G'], products: ['epx-prod-1', 'epx-prod-2', 'epx-prod-3'] } },
          { id: 'elx-p-bundle', type: 'bundle-offer', visible: true, props: { title: 'Pack bureau', productIds: ['epx-prod-1', 'epx-prod-2', 'epx-prod-3'], discountLabel: 'Remise pack dans le panier' } },
          { id: 'elx-p-related', type: 'related-products', visible: true, props: { title: 'À associer', limit: 8, columns: 4 } },
        ],
        category: [
          {
            id: 'elx-cat-count',
            type: 'countdown-timer',
            visible: true,
            props: {
              title: 'Réassort catégorie',
              subtitle: 'Les références populaires reviennent sous 48 h.',
              targetDate: '2026-12-31T23:59:00',
              expiredText: 'Compteur terminé — voir la boutique.',
              backgroundColor: '#1e3a8a',
            },
          },
          {
            id: 'elx-cat-trust',
            type: 'trust-strip',
            visible: true,
            props: {
              items: [
                { icon: 'ShieldCheck', title: 'Garantie', text: 'Détail sur chaque fiche.' },
                { icon: 'Truck', title: 'Suivi', text: 'SMS dès l’étiquette.' },
                { icon: 'Package', title: 'Neuf scellé', text: 'Photo sur demande.' },
              ],
            },
          },
          {
            id: 'elx-cat-filters',
            type: 'filters-sidebar',
            visible: true,
            props: { enablePrice: true, enableCategory: true, enableRating: true },
          },
          {
            id: 'elx-cat-carousel',
            type: 'product-carousel',
            visible: true,
            props: {
              title: 'Top segment',
              subtitle: 'Les références les plus consultées dans cette catégorie.',
              limit: 8,
              autoplay: false,
              showArrows: true,
            },
          },
          { id: 'elx-cat-grid', type: 'category-grid', visible: true, props: {} },
        ],
        cart: [
          {
            id: 'elx-cart-lines',
            type: 'cart-lines',
            visible: true,
            props: {},
          },
          {
            id: 'elx-cart-summary',
            type: 'cart-summary',
            visible: true,
            props: {},
          },
        ],
        checkout: [
          {
            id: 'elx-checkout-form',
            type: 'checkout-form',
            visible: true,
            props: {},
          },
        ],
        search: [
          {
            id: 'elx-search-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Garantie · Livraison 24–48 h',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#f8fafc',
            },
          },
          {
            id: 'elx-search-results',
            type: 'search-results',
            visible: true,
            props: {},
          },
          {
            id: 'elx-search-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Populaire en ce moment',
              subtitle: '',
              collectionId: '',
              limit: 4,
              columns: 4,
              viewAllLabel: 'Voir tout',
              viewAllLink: '/shop',
            },
          },
        ],
        account: [
          {
            id: 'elx-account-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Espace compte sécurisé — suivez vos commandes et vos adresses enregistrées.',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#e2e8f0',
            },
          },
        ],
        faq: [
          {
            id: 'elx-faq-intro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Réponses courtes sur livraison et garantie. Pour un dossier technique (numéro de série, photo thermique), écrivez-nous — le bouton ci-dessous mène au même interlocuteur que le formulaire.',
            },
          },
          {
            id: 'elx-faq',
            type: 'faq-accordion',
            visible: true,
            props: {
              title: 'Questions fréquentes',
              subtitle: 'Réponses',
              items: [
                {
                  question: 'Délai de livraison ?',
                  answer: '24–48 h ouvrées.',
                },
              ],
            },
          },
          {
            id: 'elx-faq-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Passer au canal prioritaire',
              text: 'WhatsApp: file dédiée SAV, pièces jointes et captures acceptées.',
              buttonText: 'Contacter le SAV',
              buttonLink: '/contact',
            },
          },
          {
            id: 'elx-faq-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Ils ont obtenu une réponse claire',
              subtitle: 'Après-vente & livraison',
              items: [
                {
                  name: 'Karim',
                  role: 'Acheteur vérifié',
                  text: 'Délai annoncé tenu, ticket SAV répondu le jour même.',
                  rating: 5,
                },
                {
                  name: 'Selma',
                  role: 'Config portable',
                  text: 'On m’a expliqué la garantie avec numéro de série à l’appui.',
                  rating: 5,
                },
              ],
            },
          },
        ],
        about: [
          {
            id: 'elx-about',
            type: 'rich-text',
            visible: true,
            props: {
              title: 'À propos',
              text: 'Nous testons chaque référence avant qu’elle n’apparaisse dans le catalogue.',
            },
          },
          {
            id: 'elx-about-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
        ],
        contact: [
          {
            id: 'elx-contact-hero',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/premium-electronics-v1/elx-laboratoire-section.png',
              imageAlt: 'Contrôle qualité avant expédition',
              title: 'Lignée directe service & vérification',
              text: 'Indiquez référence produit, preuve d’achat et, si besoin, numéro de commande. Les captures d’écran accélèrent le diagnostic.',
              ctaText: 'Voir garanties & critères',
              ctaLink: '/faq',
              layout: 'image-right',
              imageAspect: 'landscape',
            },
          },
          {
            id: 'elx-contact-micro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Réponse e-mail sous 24–48 h ouvrées. Pour blocage livreur ou colis en retard, joignez le suivi transporteur.',
            },
          },
          {
            id: 'elx-contact-info',
            type: 'contact-info',
            visible: true,
            props: {
              title: 'Réactivité mesurable',
              subtitle: 'Créneaux alignés sur nos promesses catalogue — traitement des tickets du lundi au dimanche.',
              phone: '+216 XX XXX XXX',
              email: 'bonjour@boutique.tn',
              workingHours: '9h–21h, 7j/7',
              address: '',
              showIcons: true,
              sectionLayout: 'grid',
            },
          },
          {
            id: 'elx-contact-form',
            type: 'contact-form',
            visible: true,
            props: {
              title: 'Ticket technique & SAV',
              subtitle: 'Nom, e-mail et message : décrivez le symptôme en quelques lignes factuelles.',
              showPhone: false,
              showSubject: false,
              buttonText: 'Envoyer le ticket',
            },
          },
          {
            id: 'elx-contact-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'File rapide WhatsApp',
              text: 'Idéal pour photos d’étiquettes, codes-barres et suivi en temps réel.',
              buttonText: 'Ouvrir WhatsApp',
              buttonLink: 'https://wa.me/21600000000',
            },
          },
          {
            id: 'elx-contact-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
          {
            id: 'elx-contact-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Confiance après achat',
              items: [
                {
                  name: 'Amine',
                  role: 'Setup gaming',
                  text: 'Réponse sur la garantie en moins d’une journée.',
                  rating: 5,
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'editorial-fashion-v1',
    name: 'Horlogerie — maison & accessoires',
    description: 'Expérience luxe : montres et maroquinerie, présentation épurée, image shoppable et galeries éditoriales.',
    niche: 'Horlogerie',
    thumbnail: '/templates/editorial-fashion-v1/vis-thumb-800.webp',
    layout: {
      theme: {
        id: 'luxury',
        primary: 'hsl(43 42% 58%)',
        secondary: 'hsl(36 22% 86%)',
        backgroundColor: '#121110',
        font: 'Cormorant Garamond',
        radius: '1rem',
        ui: {
          productCard: 'luxury',
          buttons: 'pill',
          spacing: 'airy',
          headingStyle: 'editorial',
          font: 'serif',
          density: 'comfortable',
          mode: 'dark',
          radius: 'xl',
        },
      } as any,
      header: {
        props: {
          storeName: null,
          menuItems: [
            { label: 'Accueil', link: '/' },
            { label: 'Collection', link: '/shop' },
            { label: 'Nouveautés', link: '/category/nouveautes' },
            { label: 'Concierge', link: '/contact' },
          ],
          showSearch: true,
          showCart: true,
          sticky: true,
          variant: 'minimal',
        },
      },
      footer: {
        props: {
          storeName: true,
          description: 'Présentation digne d’une manufacture—petites séries, accompagnement personnalisé.',
          copyright: `© ${new Date().getFullYear()} Maison`,
          columns: [{ title: 'Boutique', links: [{ label: 'Toute la collection', link: '/shop' }] }, { title: 'Service', links: [{ label: 'Questions fréquentes', link: '/faq' }] }],
          socialLinks: { instagram: 'https://instagram.com' },
          showPaymentIcons: true,
        },
      },
      pages: {
        home: [
          { id: 'fsh-h-announce', type: 'announcement-bar', visible: true, props: { text: 'Attribution privée · Authenticité certifiée · Concierge bracelet & taille de poignet', link: '/shop', backgroundColor: '#e8e4dd', textColor: '#1a1816' } },
          { id: 'fsh-h-banner', type: 'image-banner', visible: true, props: { image: '/templates/editorial-fashion-v1/vis-banner-lookbook.png', title: 'Sélection atelier', subtitle: 'Acier, cuir, lumière—réduit à l’essentiel.', ctaLabel: 'Découvrir', ctaLink: '/shop', overlayOpacity: 40, minHeight: 'lg', textAlign: 'center' } },
          { id: 'fsh-h-hero', type: 'hero', visible: true, props: { headline: 'L’art du temps', subtitle: 'Montres et accessoires—l’essentiel, rien de plus.', ctaLabel: 'Voir la collection', anchorId: 'fsh-grid', backgroundImage: '/templates/editorial-fashion-v1/vis-hero-main.png', layout: 'centered', overlayOpacity: 48, textColor: '#f5f0e8' } },
          { id: 'fsh-h-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'Truck', title: 'Livraison soignée', text: 'Colis discret, transport assuré.' }, { icon: 'ShieldCheck', title: 'Provenance', text: 'Papiers et suivi sur demande.' }, { icon: 'Package', title: 'Conciergerie', text: 'Bracelet, entretien, ajustage—ligne directe.' }] } },
          {
            id: 'fsh-h-runway-video',
            type: 'video-block',
            visible: true,
            props: {
              videoUrl: '/templates/editorial-fashion-v1/vis-runway.mp4',
              poster: '/templates/editorial-fashion-v1/vis-video-poster.png',
              autoplay: false,
              muted: true,
              loop: false,
              overlayText: 'Le geste et la matière—main, outil, poli',
              ctaLabel: 'Voir la collection',
              ctaLink: '/shop',
            },
          },
          { id: 'editorial-fashion-v1/', type: 'shoppable-image', visible: true, props: { image: '/templates/editorial-fashion-v1/vis-hero-main.png', hotspots: [] } },
          { id: 'fsh-h-spacer', type: 'spacer', visible: true, props: { size: 'lg' } },
          { id: 'fsh-h-promo-grid', type: 'promo-grid', visible: true, props: { items: [{ title: 'Signature', subtitle: 'La pièce qui définit la ligne.', image: '/templates/editorial-fashion-v1/vis-promo-a.png', ctaLabel: 'Découvrir', ctaLink: '/shop', size: 'large' }, { title: 'Cuir', subtitle: 'Bracelets et petites pièces—tannage et piqûre.', image: '/templates/editorial-fashion-v1/vis-promo-b.png', ctaLabel: 'Parcourir', ctaLink: '/shop', size: 'small' }, { title: 'Détails', subtitle: 'Couronnes, verre, finitions—là où le geste compte.', image: '/templates/editorial-fashion-v1/vis-promo-c.png', ctaLabel: 'Voir', ctaLink: '/shop', size: 'small' }, { title: 'Quotidien', subtitle: 'Compagnons discrets pour chaque instant.', image: '/templates/editorial-fashion-v1/vis-promo-d.png', ctaLabel: 'Acheter', ctaLink: '/shop', size: 'small' }] } },
          { id: 'fsh-h-gallery', type: 'image-gallery', visible: true, props: { images: ['/templates/editorial-fashion-v1/vis-edi-gal-01.png', '/templates/editorial-fashion-v1/vis-edi-gal-02.png', '/templates/editorial-fashion-v1/vis-edi-gal-03.png', '/templates/editorial-fashion-v1/vis-edi-gal-04.png', '/templates/editorial-fashion-v1/vis-edi-gal-05.png', '/templates/editorial-fashion-v1/vis-edi-gal-06.png'], layout: 'masonry', columns: 3 } },
          { id: 'fsh-h-featured', type: 'featured-collection', visible: true, props: { title: 'Réservée à quelques-uns', subtitle: 'Meilleure vente · Édition limitée—sélection qui tourne.', collectionId: '', limit: 6, columns: 3, viewAllLabel: 'Toute la collection', viewAllLink: '/shop' } },
          { id: 'fsh-h-carousel', type: 'product-carousel', visible: true, props: { title: 'Icônes & héritage', subtitle: 'Séries courtes—quand c’est parti, c’est parti.', limit: 10, autoplay: true, showArrows: true } },
          { id: 'fsh-h-testimonials', type: 'testimonials', visible: true, props: { title: 'Au poignet de ceux qui choisissent une fois', subtitle: 'Collectionneurs & créatifs', items: [{ name: 'Mira K.', role: 'Directrice artistique', text: 'Le rendu du boîtier et du verre est saisissant en lumière basse.', rating: 5 }, { name: 'Selim R.', role: 'Photographe', text: 'Service et délais alignés sur une maison sérieuse.', rating: 5 }, { name: 'Lina D.', role: 'Cliente', text: 'Le concierge bracelet a évité le mauvais choix de taille.', rating: 5 }] } },
          { id: 'fsh-h-newsletter', type: 'newsletter', visible: true, props: { title: 'Le temps, autrement', subtitle: 'Invitations aux lancements, heures privées et programmes d’entretien.', placeholder: 'E-mail', buttonText: 'Demander l’accès', backgroundColor: '#0a0908', backgroundImage: '', overlayOpacity: 0 } },
        ],
        shop: [
          { id: 'fsh-s-announce', type: 'announcement-bar', visible: true, props: { text: 'Contrôle bracelet offert · Retours 30 jours sur pièces non portées · Carte d’authenticité incluse', link: '/faq', backgroundColor: '', textColor: '' } },
          { id: 'fsh-s-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'Truck', title: 'Expédition 48 h', text: 'Références en stock, colis assuré.' }, { icon: 'ShieldCheck', title: 'Garantie & service', text: 'Enregistrement de la pièce sur demande.' }, { icon: 'Zap', title: 'Expertise poignet', text: 'Entre-corne, largeur de bracelet—chat concierge.' }] } },
          { id: 'fsh-s-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'fsh-s-filters', type: 'filters-sidebar', visible: true, props: { enablePrice: true, enableCategory: true, enableRating: true } },
          { id: 'fsh-s-banner', type: 'image-banner', visible: true, props: { image: '/templates/editorial-fashion-v1/vis-shop-banner.png', title: 'La boutique', subtitle: 'Chaque fiche : matériaux, mouvement, entretien.', ctaLabel: 'Découvrir', ctaLink: '#fsh-grid', overlayOpacity: 42, minHeight: 'lg', textAlign: 'center' } },
          { id: 'fsh-s-carousel', type: 'product-carousel', visible: true, props: { title: 'Choix de la rédaction', subtitle: 'Coup de projecteur Meilleure vente.', limit: 12, autoplay: false, showArrows: true } },
          { id: 'fsh-s-featured', type: 'featured-collection', visible: true, props: { title: 'Éditions limitées', subtitle: 'Petites séries—réassort rare.', collectionId: '', limit: 8, columns: 4, viewAllLabel: 'Tout voir', viewAllLink: '/shop' } },
          { id: 'fsh-s-grid', type: 'product-grid', visible: true, props: { title: 'Toute la collection', subtitle: 'Acier, or, cuir—filtres et fiches précises.', limit: 40, columns: 4, anchorId: 'fsh-grid' } },
          { id: 'fsh-s-newsletter', type: 'newsletter', visible: true, props: { title: 'Les premiers informés', subtitle: 'Allocations, ateliers d’entretien, nouveautés.', placeholder: 'E-mail', buttonText: 'S’inscrire', backgroundColor: '#1c1917', backgroundImage: '', overlayOpacity: 20 } },
        ],
        product: [
          { id: 'fsh-p-details', type: 'product-details', visible: true, props: { highlights: ['Finitions de boîtier affinées à la main', 'Verre saphir anti-reflet', 'Carte de garantie & référence série'], trustShipping: 'Emballage discret, transport assuré.', trustReturns: 'Retours 30 jours si non portée, coffret complet.', trustPayment: 'Cartes sécurisées et paiement à la livraison selon zone.' } },
          { id: 'fsh-p-tabs', type: 'product-tabs', visible: true, props: { tabs: [{ label: 'Boîtier & mouvement', content: 'Dimensions, étanchéité, réserve de marche et calibre (selon modèle).' }, { label: 'Vision design', content: 'La place de la pièce dans la ligne maison et ses signatures visuelles.' }, { label: 'Entretien', content: 'Cuirs, métaux, stockage—recommandations du atelier.' }] } },
          { id: 'fsh-p-shop-img', type: 'shoppable-image', visible: true, props: { image: '/templates/editorial-fashion-v1/vis-pdp.png', hotspots: [] } },
          { id: 'fsh-p-compare', type: 'comparison-table', visible: true, props: { title: 'Comparer les finitions', features: ['Verre', 'Boîtier', 'Bracelet', 'Étanchéité', 'Garantie'], products: ['edt-prod-1', 'edt-prod-2', 'edt-prod-3'] } },
          { id: 'fsh-p-bundle', type: 'bundle-offer', visible: true, props: { title: 'Trio maison', productIds: ['edt-prod-1', 'edt-prod-2', 'edt-prod-3'], discountLabel: '−15 % trio signature — cadran, bracelet & profil boîtier' } },
          { id: 'fsh-p-related', type: 'related-products', visible: true, props: { title: 'Les paires qui comptent', limit: 6, columns: 3 } },
        ],
        category: [
          {
            id: 'fsh-cat-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Authenticité · Coffret complet annoncé · Concierge bracelet sur WhatsApp',
              link: '/faq',
              backgroundColor: '#1c1917',
              textColor: '#fafaf9',
            },
          },
          {
            id: 'fsh-cat-gallery',
            type: 'image-gallery',
            visible: true,
            props: {
              layout: 'masonry',
              columns: 3,
              images: [
                '/templates/editorial-fashion-v1/vis-edi-gal-05.png',
                '/templates/editorial-fashion-v1/vis-edi-gal-02.png',
                '/templates/editorial-fashion-v1/vis-edi-gal-03.png',
              ],
            },
          },
          { id: 'fsh-cat-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'fsh-cat-grid', type: 'category-grid', visible: true, props: {} },
        ],
        cart: [
          {
            id: 'fsh-cart-lines',
            type: 'cart-lines',
            visible: true,
            props: {},
          },
          {
            id: 'fsh-cart-summary',
            type: 'cart-summary',
            visible: true,
            props: {},
          },
        ],
        checkout: [
          {
            id: 'fsh-checkout-form',
            type: 'checkout-form',
            visible: true,
            props: {},
          },
        ],
        search: [
          {
            id: 'fsh-search-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Recherche dans la collection',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#f8fafc',
            },
          },
          {
            id: 'fsh-search-results',
            type: 'search-results',
            visible: true,
            props: {},
          },
          {
            id: 'fsh-search-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Coup de cœur',
              subtitle: 'Meilleure vente · Nouveauté',
              collectionId: '',
              limit: 4,
              columns: 4,
              viewAllLabel: 'Voir tout',
              viewAllLink: '/shop',
            },
          },
        ],
        account: [
          {
            id: 'fsh-account-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Espace compte sécurisé — commandes, garanties et adresses de livraison.',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#e2e8f0',
            },
          },
        ],
        faq: [
          {
            id: 'fsh-faq-intro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Objets de précision, réponses précises : étanchéité, garantie, bracelet et entretien. Pour un avis au poignet, écrivez au Concierge.',
            },
          },
          {
            id: 'fsh-faq',
            type: 'faq-accordion',
            visible: true,
            props: {
              title: 'Questions fréquentes',
              subtitle: 'Propriété & entretien',
              items: [
                {
                  question: 'Étanchéité et usage quotidien ?',
                  answer: 'Chaque fiche indique la pression testée ; évitez douche et choc thermique sauf modèle outil.',
                },
                {
                  question: 'Garantie et carnet ?',
                  answer: 'Carte de garantie avec référence ; extension possible selon programme atelier.',
                },
                {
                  question: 'Changer de bracelet ?',
                  answer: 'Largeur entre-cornes indiquée ; le concierge valide la compatibilité avant commande.',
                },
              ],
            },
          },
          {
            id: 'fsh-faq-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Écrire à la maison',
              text: 'WhatsApp ou message : réglage bracelet, finitions, retours et envoi du coffret complet.',
              buttonText: 'Ouvrir la page contact',
              buttonLink: '/contact',
            },
          },
          {
            id: 'fsh-faq-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Conseil reçu, choix affiné',
              items: [
                {
                  name: 'Yasmine',
                  role: 'Cliente',
                  text: 'Réponse claire sur le calibre et le bracelet avant d’acheter.',
                  rating: 5,
                },
              ],
            },
          },
          {
            id: 'fsh-faq-closing',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/editorial-fashion-v1/vis-promo-a.png',
              imageAlt: 'Montre et détails atelier',
              title: 'Après la FAQ',
              text: 'Même exigence que sur la vitrine—posez votre question avant de choisir la référence.',
              ctaText: 'Retour à la boutique',
              ctaLink: '/shop',
              layout: 'image-left',
              imageAspect: 'portrait',
            },
          },
        ],
        about: [
          {
            id: 'fsh-about',
            type: 'rich-text',
            visible: true,
            props: {
              title: 'La maison',
              text: 'Nous photographions et spécifions chaque pièce comme un carnet de manufacture—petites séries, provenance claire, humain au bout du fil.',
            },
          },
          {
            id: 'fsh-about-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
        ],
        contact: [
          {
            id: 'fsh-contact-hero',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/editorial-fashion-v1/vis-banner-lookbook.png',
              imageAlt: 'Atelier horloger',
              title: 'Concierge & entretien',
              text: 'Référence modèle, tour de poignet, entre-cornes : nous lisons chaque message avec la même rigueur qu’une fiche technique.',
              ctaText: 'FAQ & entretien',
              ctaLink: '/faq',
              layout: 'image-left',
              imageAspect: 'portrait',
            },
          },
          {
            id: 'fsh-contact-micro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Indiquez la référence, le délai souhaité et, si besoin, le tour de poignet—bref et exact.',
            },
          },
          {
            id: 'fsh-contact-info',
            type: 'contact-info',
            visible: true,
            props: {
              title: 'Atelier',
              subtitle: 'Créneaux ouverts selon calendrier manufacture—réponse sous 24 h ouvrées en général.',
              phone: '+216 XX XXX XXX',
              email: 'bonjour@boutique.tn',
              workingHours: '10h–19h, lun–sam',
              address: '',
              showIcons: true,
              sectionLayout: 'grid',
            },
          },
          {
            id: 'fsh-contact-form',
            type: 'contact-form',
            visible: true,
            props: {
              title: 'Écrire au concierge',
              subtitle: 'Nom, e-mail et message — nous revenons vers vous avec la suite : bracelet, entretien ou échange.',
              showPhone: false,
              showSubject: false,
              buttonText: 'Envoyer',
            },
          },
          {
            id: 'fsh-contact-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Ligne prioritaire',
              text: 'Pour un ajustage urgent ou une question veille de voyage : WhatsApp concierge.',
              buttonText: 'WhatsApp concierge',
              buttonLink: 'https://wa.me/21600000000',
            },
          },
          {
            id: 'fsh-contact-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
          {
            id: 'fsh-contact-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Avant de passer commande',
              items: [
                {
                  name: 'Mira K.',
                  role: 'Collectionneuse',
                  text: 'Conseil clair sur le verre et le boîtier avant l’achat.',
                  rating: 5,
                },
                {
                  name: 'Selim',
                  role: 'Créatif',
                  text: 'Réponses rapides sur délais et coffret.',
                  rating: 5,
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'single-product-funnel-v1',
    name: 'Produit unique — tunnel de conversion',
    description: 'Tunnel mono-SKU : vidéo, CTA fixe, pack, urgence, preuves.',
    niche: 'D2C',
    thumbnail: '/templates/single-product-funnel-v1/thumb-800.webp',
    layout: {
      theme: {
        id: 'bold-arabic',
        primary: 'hsl(262 83% 58%)',
        secondary: 'hsl(270 40% 96%)',
        backgroundColor: '#faf5ff',
        font: 'Inter',
        radius: '0.75rem',
        ui: {
          productCard: 'bold',
          buttons: 'ghost',
          spacing: 'compact',
          headingStyle: 'bold',
          density: 'tight',
          font: 'sans',
          mode: 'light',
          radius: 'md',
        },
      } as any,
      header: {
        props: {
          storeName: null,
          menuItems: [{ label: 'Produit', link: '/product/fn-prod-1' }],
          showSearch: false,
          showCart: true,
          sticky: true,
          variant: 'minimal',
        },
      },
      footer: {
        props: {
          storeName: true,
          description: 'Une référence phare — support au millimètre.',
          copyright: `© ${new Date().getFullYear()}`,
          columns: [{ title: 'Infos', links: [{ label: 'Questions fréquentes', link: '/faq' }] }],
          socialLinks: {},
          showPaymentIcons: true,
        },
      },
      pages: {
        home: [
          { id: 'spl-h-announce', type: 'announcement-bar', visible: true, props: { text: 'Tarif fondateur verrouillé — compte à rebours en direct', link: '/product/fn-prod-1', backgroundColor: '#111827', textColor: '#fef3c7' } },
          { id: 'spl-h-hero', type: 'hero', visible: true, props: { headline: 'Un produit. Une finition obsessionnelle.', subtitle: 'Mise en route accompagnée, micrologiciel à vie, engagement 60 jours.', ctaLabel: 'Réserver', ctaLink: '/product/fn-prod-1', anchorId: 'spl-proof', backgroundImage: '/templates/single-product-funnel-v1/vis-funnel-hero.svg', layout: 'centered', overlayOpacity: 58, textColor: '#ffffff' } },
          { id: 'spl-h-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'ShieldCheck', title: 'Garantie 60 jours', text: 'Remboursement si les benchmarks ne tiennent pas.' }, { icon: 'Truck', title: 'Livraison concierge', text: 'SMS la veille.' }, { icon: 'Zap', title: 'Micrologiciel à vie', text: 'Mises à jour OTA sans limite.' }] } },
          { id: 'spl-h-countdown', type: 'countdown-timer', visible: true, props: { title: 'Fenêtre fondateur', subtitle: 'Le prix augmente à zéro.', targetDate: '2026-12-31T23:59:00', expiredText: 'Terminé — rejoignez la liste d’attente.', backgroundColor: '#7c3aed' } },
          { id: 'spl-h-video', type: 'video-block', visible: true, props: { videoUrl: '/templates/single-product-funnel-v1/realistic-product-usage-scene.mp4', poster: '/templates/single-product-funnel-v1/vis-funnel-poster.svg', autoplay: false, muted: true, loop: false, overlayText: 'Déballage 60 s — unité fondateur', ctaText: 'Voir les preuves', ctaLink: '/product/fn-prod-1' } },
          { id: 'spl-h-bundle', type: 'bundle-offer', visible: true, props: { title: 'Pack fondateur', productIds: ['fn-prod-1', 'fn-prod-2', 'fn-prod-3'], discountLabel: '−18 % kit + garantie + tapis' } },
          { id: 'spl-h-shop-img', type: 'shoppable-image', visible: true, props: { image: '/templates/single-product-funnel-v1/vis-funnel-shop.svg', hotspots: [{ x: 46, y: 52, productId: 'fn-prod-1' }, { x: 22, y: 58, productId: 'fn-prod-1' }] } },
          { id: 'spl-h-compare', type: 'comparison-table', visible: true, props: { title: 'Produit phare vs alternatives', features: ['Persistance d’écran testée', 'Fiche de référence', 'QR conciergerie', 'Micrologiciel à vie', '60 jours'], products: ['fn-prod-1', 'fn-prod-2', 'fn-prod-3'] } },
          { id: 'spl-h-testimonials', type: 'testimonials', visible: true, props: { title: 'Voix bêta', subtitle: 'Testeurs', items: [{ name: 'Noah P.', role: 'Testeur', text: 'Les chiffres correspondaient au cahier des charges.', rating: 5, avatar: '/templates/single-product-funnel-v1/vis-funnel-t1.svg' }, { name: 'Yasmine L.', role: 'Designer', text: 'Emballage digne d’une marque premium.', rating: 5, avatar: '/templates/single-product-funnel-v1/vis-funnel-t3.svg' }, { name: 'Omar F.', role: 'Acheteur', text: 'Réponse WhatsApp en 6 min.', rating: 5, avatar: '/templates/single-product-funnel-v1/vis-funnel-t2.svg' }] } },
          { id: 'spl-h-faq', type: 'faq-accordion', visible: true, props: { title: 'Objections', subtitle: 'Avant de payer', backgroundImage: '/templates/single-product-funnel-v1/vis-funnel-faq.svg', overlayOpacity: 0, sectionAlignment: 'center', items: [{ question: 'Pas à l’aise avec la technique ?', answer: 'Parcours imprimés avec QR.' }, { question: 'Paiement en plusieurs fois ?', answer: 'Affiché au paiement si éligible.' }] } },
          { id: 'spl-h-sticky', type: 'sticky-cta', visible: true, props: { text: 'Verrouiller le tarif fondateur', buttonLabel: 'Acheter maintenant', link: '/product/fn-prod-1', visibleOn: 'mobile' } },
          { id: 'spl-h-newsletter', type: 'newsletter', visible: true, props: { title: 'Liste d’attente vague 2', subtitle: 'Pas de spam.', placeholder: 'E-mail', buttonText: 'Rejoindre', backgroundColor: '#0f172a', backgroundImage: '', overlayOpacity: 30 } },
          {
            id: 'spl-h-bundle-flatlay',
            type: 'image-banner',
            visible: false,
            props: {
              image: '/templates/single-product-funnel-v1/vis-funnel-bundle.svg',
              title: 'Pack fondateur',
              subtitle: 'Unité, câble tressé, housse rigide, carte de bienvenue — tout ce qui compte dans la boîte.',
              ctaText: 'Composer le pack',
              ctaLink: '/product/fn-prod-1',
              overlayOpacity: 44,
              minHeight: 'md',
              textAlign: 'center',
            },
          },
          {
            id: 'spl-h-compare-visual',
            type: 'image-banner',
            visible: false,
            props: {
              image: '/templates/single-product-funnel-v1/vis-funnel-compare.svg',
              title: 'Produit phare vs alternatives',
              subtitle: 'Vue synthétique — tolérances, thermique, bruit et efficacité.',
              ctaText: 'Voir la fiche',
              ctaLink: '/product/fn-prod-1',
              overlayOpacity: 12,
              minHeight: 'lg',
              textAlign: 'center',
            },
          },
          { id: 'spl-h-carousel', type: 'product-carousel', visible: false, props: { title: 'Options dans la même session', subtitle: 'Kit d’entretien, garantie, tapis.', limit: 8, autoplay: true, showArrows: true } },
          { id: 'spl-h-rich', type: 'rich-text', visible: false, props: { title: 'Pourquoi une seule référence ?', text: 'Nous faisons un burn-in sur chaque unité et photographions avant expédition.' } },
        ],
        shop: [
          { id: 'spl-s-announce', type: 'announcement-bar', visible: true, props: { text: 'La sélection courte soutient le tunnel phare', link: '/product/fn-prod-1', backgroundColor: '#111827', textColor: '#fef9c3' } },
          { id: 'spl-s-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'Package', title: 'Économies pack', text: 'Les remises se cumulent au paiement.' }, { icon: 'ShieldCheck', title: 'Même garantie', text: 'Sur les accessoires avec le produit phare.' }, { icon: 'Truck', title: 'Expédition rapide', text: 'Packs sous 24 h.' }] } },
          { id: 'spl-s-filters', type: 'filters-sidebar', visible: false, props: { enablePrice: true, enableCategory: true, enableRating: false } },
          { id: 'spl-s-carousel', type: 'product-carousel', visible: false, props: { title: 'Voie montée en gamme', subtitle: 'Ajouts de session.', limit: 10, autoplay: true, showArrows: false } },
          { id: 'spl-s-featured', type: 'featured-collection', visible: false, props: { title: 'Pack de démarrage', subtitle: 'Aligné sur la fiche produit.', collectionId: '', limit: 6, columns: 3, viewAllLabel: 'Phare', viewAllLink: '/product/fn-prod-1' } },
          { id: 'spl-s-grid', type: 'product-grid', visible: false, props: { title: 'Sélection courte', subtitle: 'Grille dense.', limit: 12, columns: 3, anchorId: 'spl-shop' } },
          { id: 'spl-s-sticky', type: 'sticky-cta', visible: true, props: { text: 'Paiement en moins de 60 s', buttonLabel: 'Payer', link: '/checkout', visibleOn: 'all' } },
        ],
        product: [
          { id: 'spl-p-video', type: 'video-block', visible: true, props: { videoUrl: '/templates/single-product-funnel-v1/realistic-product-usage-scene.mp4', poster: '/templates/single-product-funnel-v1/vis-funnel-poster.svg', autoplay: false, muted: true, loop: false, overlayText: 'En mouvement — visite labo', ctaText: 'Faire défiler pour acheter', ctaLink: '/product/fn-prod-1' } },
          { id: 'spl-p-details', type: 'product-details', visible: true, props: { highlights: ['Persistance d’écran 24 h', 'Fiche de référence', 'QR conciergerie'], trustShipping: 'Express + créneaux premium.', trustReturns: 'Garantie performance 60 jours.', trustPayment: 'Cartes, portefeuilles, paiement à la livraison.' } },
          { id: 'spl-p-tabs', type: 'product-tabs', visible: true, props: { tabs: [{ label: 'Inclus', content: 'Station, câble, benchmarks, note.' }, { label: 'Fiche technique', content: 'PDF pour achats.' }, { label: 'Fiscalité', content: 'Calculateur de droits au paiement.' }] } },
          {
            id: 'spl-p-compare-visual',
            type: 'image-banner',
            visible: false,
            props: {
              image: '/templates/single-product-funnel-v1/vis-funnel-compare.svg',
              title: 'Fondateur vs grande distribution',
              subtitle: 'Même comparaison que sur l’accueil.',
              ctaText: 'Retour boutique',
              ctaLink: '/product/fn-prod-1',
              overlayOpacity: 12,
              minHeight: 'md',
              textAlign: 'center',
            },
          },
          { id: 'spl-p-compare', type: 'comparison-table', visible: true, props: { title: 'Fondateur vs grande distribution', features: ['Garantie', 'Concierge', 'Impression benchmark', 'Service premium', 'FW prioritaire'], products: ['fn-prod-1', 'fn-prod-2', 'fn-prod-3'] } },
          { id: 'spl-p-bundle', type: 'bundle-offer', visible: true, props: { title: 'Options groupées', productIds: ['fn-prod-1', 'fn-prod-2', 'fn-prod-3'], discountLabel: 'Pack auto dans le panier' } },
          { id: 'spl-p-related', type: 'related-products', visible: false, props: { title: 'Encore en navigation ?', limit: 4, columns: 2 } },
          { id: 'spl-p-sticky', type: 'sticky-cta', visible: true, props: { text: 'Écrire aux fondateurs', buttonLabel: 'WhatsApp', link: 'https://wa.me/21600000000', visibleOn: 'mobile' } },
        ],
        category: [
          {
            id: 'spl-cat-promo',
            type: 'promo-banner',
            visible: true,
            props: {
              text: 'Sélection courte autour du produit phare — mêmes garanties et support que sur l’accueil.',
              highlight: 'Fondateur',
              backgroundColor: '#111827',
              textColor: '#fef9c3',
              ctaLabel: 'Voir le phare',
              ctaLink: '/product/fn-prod-1',
              showIcon: true,
            },
          },
          {
            id: 'spl-cat-count',
            type: 'countdown-timer',
            visible: true,
            props: {
              title: 'Fenêtre tarif',
              subtitle: 'Alignée sur la home — même minute partout.',
              targetDate: '2026-12-31T23:59:00',
              expiredText: 'Terminé — consulter la boutique.',
              backgroundColor: '#7c3aed',
            },
          },
          { id: 'spl-cat-grid', type: 'category-grid', visible: false, props: {} },
        ],
        cart: [
          {
            id: 'spl-cart-lines',
            type: 'cart-lines',
            visible: true,
            props: {},
          },
          {
            id: 'spl-cart-summary',
            type: 'cart-summary',
            visible: true,
            props: {},
          },
        ],
        checkout: [
          {
            id: 'spl-checkout-form',
            type: 'checkout-form',
            visible: true,
            props: {},
          },
        ],
        search: [
          { id: 'spl-search-announce', type: 'announcement-bar', visible: true, props: { text: 'Rechercher des options', link: '/product/fn-prod-1', backgroundColor: '#0f172a', textColor: '#f8fafc' } },
          { id: 'spl-search-results', type: 'search-results', visible: true, props: {} },
          {
            id: 'spl-search-featured',
            type: 'featured-collection',
            visible: false,
            props: { title: 'Options populaires', subtitle: '', collectionId: '', limit: 4, columns: 4, viewAllLabel: 'Voir le produit', viewAllLink: '/product/fn-prod-1' },
          },
        ],
        account: [
          {
            id: 'spl-account-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Espace compte sécurisé — suivez vos commandes et vos adresses enregistrées.',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#e2e8f0',
            },
          },
        ],
        faq: [
          {
            id: 'spl-faq-intro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Réponses ultra-courtes pour ne pas vous détourner du checkout — la question ci-dessous est volontairement unique. Besoin de surcharger le benchmark ? Bouton en bas.',
            },
          },
          {
            id: 'spl-faq',
            type: 'faq-accordion',
            visible: true,
            props: {
              title: 'Questions fréquentes',
              subtitle: 'Fondateur',
              items: [
                {
                  question: 'Précommande ?',
                  answer: 'Stock en temps réel — expédition 48 h.',
                },
              ],
            },
          },
          {
            id: 'spl-faq-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Réponse prioritaire avant paiement',
              text: 'Un humain valide stock et délai — message ou WhatsApp, même fichier que le formulaire.',
              buttonText: 'Obtenir une réponse prioritaire',
              buttonLink: '/contact',
            },
          },
          {
            id: 'spl-faq-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Ils ont franchi le paywall',
              subtitle: 'Beta & acheteurs',
              items: [
                {
                  name: 'Omar F.',
                  role: 'Acheteur',
                  text: 'Réponse WhatsApp en 6 min — j’ai validé la précommande.',
                  rating: 5,
                },
              ],
            },
          },
        ],
        about: [
          {
            id: 'spl-about',
            type: 'rich-text',
            visible: true,
            props: {
              title: 'Mission',
              text: 'Une référence perfectionnée.',
            },
          },
          {
            id: 'spl-about-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
        ],
        contact: [
          {
            id: 'spl-contact-hero',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/single-product-funnel-v1/vis-funnel-hero.svg',
              imageAlt: 'Produit phare — tunnel',
              title: 'Une question = une décision',
              text: 'Pas de file d’attente anonyme : chaque message est traité par l’équipe qui expédie les unités fondateur.',
              ctaText: 'Retour à l’offre',
              ctaLink: '/product/fn-prod-1',
              layout: 'image-right',
              imageAspect: 'landscape',
            },
          },
          {
            id: 'spl-contact-micro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Dites-nous si vous êtes déjà en panier Stripe / COD — nous priorisons les messages « avant paiement ».',
            },
          },
          {
            id: 'spl-contact-info',
            type: 'contact-info',
            visible: true,
            props: {
              title: 'Concierge funnel',
              subtitle: 'Temps de réponse cible : moins de 2 h en journée — même numéro que la fiche produit.',
              phone: '+216 XX XXX XXX',
              email: 'bonjour@boutique.tn',
              workingHours: '8h–minuit, 7j/7',
              address: '',
              showIcons: true,
              sectionLayout: 'grid',
            },
          },
          {
            id: 'spl-contact-form',
            type: 'contact-form',
            visible: true,
            props: {
              title: 'Débloquer avant achat',
              subtitle: 'Nom, e-mail, message : une ligne suffit — « stock ? », « benchmark ? », « upgrade ? ».',
              showPhone: false,
              showSubject: false,
              buttonText: 'Envoyer — réponse sous 24 h',
            },
          },
          {
            id: 'spl-contact-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Urgence fondateur',
              text: 'Hotline WhatsApp pour décider pendant que le compte à rebours tourne.',
              buttonText: 'WhatsApp immédiat',
              buttonLink: 'https://wa.me/21600000000',
            },
          },
          {
            id: 'spl-contact-trust',
            type: 'trust-strip',
            visible: true,
            props: {
              items: [
                {
                  icon: 'ShieldCheck',
                  title: 'Garantie 60 jours',
                  text: 'Même promesse que sur la page produit.',
                },
                {
                  icon: 'Zap',
                  title: 'Traitement prioritaire',
                  text: 'Routing direct équipe packaging.',
                },
                {
                  icon: 'Truck',
                  title: 'Expédition suivie',
                  text: 'SMS la veille — comme le hero tunnel.',
                },
              ],
            },
          },
          {
            id: 'spl-contact-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Pourquoi ils ont écrit avant le clic',
              items: [
                {
                  name: 'Noah P.',
                  role: 'Testeur bêta',
                  text: 'J’ai eu le benchmark en capture avant de payer.',
                  rating: 5,
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'home-decor-pinterest-v1',
    name: 'Décoration — moodboard type Pinterest',
    description: 'Inspiration d’abord : galeries maçonnées, grille promo bento, photos shoppables.',
    niche: 'Décoration',
    thumbnail: '/templates/home-decor-pinterest-v1/thumb-800.webp',
    layout: {
      theme: {
        id: 'aura',
        primary: 'hsl(25 70% 40%)',
        secondary: 'hsl(25 30% 96%)',
        backgroundColor: '#fdfcfa',
        font: 'Lora',
        radius: '0.5rem',
        ui: {
          productCard: 'minimal',
          buttons: 'secondary',
          spacing: 'normal',
          headingStyle: 'editorial',
          density: 'comfortable',
          font: 'serif',
          mode: 'light',
          radius: 'md',
        },
      } as any,
      header: {
        props: {
          storeName: null,
          menuItems: [{ label: 'Accueil', link: '/' }, { label: 'Collections', link: '/shop' }, { label: 'Questions fréquentes', link: '/faq' }],
          showSearch: true,
          showCart: true,
          sticky: true,
          variant: 'default',
        },
      },
      footer: {
        props: {
          storeName: true,
          description: 'Objets pour un quotidien plus lent.',
          copyright: `© ${new Date().getFullYear()}`,
          columns: [{ title: 'Boutique', links: [{ label: 'Tout', link: '/shop' }] }],
          socialLinks: { instagram: 'https://instagram.com' },
          showPaymentIcons: true,
        },
      },
      pages: {
        home: [
          { id: 'hdc-h-announce', type: 'announcement-bar', visible: true, props: { text: 'Studio Terra — céramiques, bouclé, lumière douce', link: '/shop', backgroundColor: '#ede9e3', textColor: '#44403c' } },
          { id: 'hdc-h-gallery-1', type: 'image-gallery', visible: true, props: { images: ['/templates/home-decor-pinterest-v1/carre-001.png', '/templates/home-decor-pinterest-v1/carre-002.png', '/templates/home-decor-pinterest-v1/carre-003.png', '/templates/home-decor-pinterest-v1/carre-004.png', '/templates/home-decor-pinterest-v1/carre-005.png'], layout: 'masonry', columns: 3 } },
          { id: 'hdc-h-hero', type: 'hero', visible: true, props: { headline: 'Des pièces superposées, des rituels plus calmes', subtitle: 'Tactile, tonal, prêt à épingler.', ctaLabel: 'Explorer', anchorId: 'hdc-board', backgroundImage: '/templates/home-decor-pinterest-v1/carre-089.png', layout: 'fullwidth', overlayOpacity: 42, textColor: '#fffbeb' } },
          { id: 'hdc-h-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'Truck', title: 'Emballage fragile', text: 'Double carton pour le verre.' }, { icon: 'ShieldCheck', title: 'Carte style', text: 'Idées de mise en scène dans la boîte.' }, { icon: 'Package', title: 'Retours 14 jours', text: 'Non utilisé, emballage d’origine.' }] } },
          { id: 'hdc-h-shop-img', type: 'shoppable-image', visible: true, props: { image: '/templates/home-decor-pinterest-v1/carre-006.png', hotspots: [{ x: 30, y: 50, productId: 'hdc-prod-1' }, { x: 62, y: 44, productId: 'hdc-prod-2' }, { x: 48, y: 72, productId: 'hdc-prod-3' }] } },
          { id: 'hdc-h-promo-grid', type: 'promo-grid', visible: true, props: { variant: 'hero-right', items: [{ title: 'Terra, sans filtre', subtitle: 'Ce que vous touchez en premier — avant le scroll.', image: '/templates/home-decor-pinterest-v1/promo-terra-sans-filtre.png', ctaLabel: 'Voir la série phare', ctaLink: '/shop', size: 'large' }, { title: 'Coupez le superflu', subtitle: 'Moins d’objets, plus de présence.', image: '/templates/home-decor-pinterest-v1/promo-coupez-le-superflu.png', ctaLabel: 'Charger la sélection', ctaLink: '/shop', size: 'small' }, { title: 'Réchauffez l’instant', subtitle: 'Plaid, lumière basse, silence.', image: '/templates/home-decor-pinterest-v1/promo-rechauffez-l-instant.png', ctaLabel: 'S’envelopper', ctaLink: '/shop', size: 'small' }, { title: 'Faites entrer le jour', subtitle: 'Verre, lin, reflets calmes.', image: '/templates/home-decor-pinterest-v1/promo-faites-entrer-le-jour.png', ctaLabel: 'Ouvrir la lumière', ctaLink: '/shop', size: 'small' }] } },
          { id: 'hdc-h-gallery-2', type: 'image-gallery', visible: true, props: { images: ['/templates/home-decor-pinterest-v1/carre-011.png', '/templates/home-decor-pinterest-v1/carre-012.png', '/templates/home-decor-pinterest-v1/carre-013.png', '/templates/home-decor-pinterest-v1/carre-016.png'], layout: 'grid', columns: 2 } },
          { id: 'hdc-h-featured', type: 'featured-collection', visible: true, props: { title: 'Meilleures ventes prêtes à épingler', subtitle: 'Salon et chambre.', collectionId: '', limit: 8, columns: 4, viewAllLabel: 'Toute la boutique', viewAllLink: '/shop' } },
          { id: 'hdc-h-carousel', type: 'product-carousel', visible: true, props: { title: 'Défilement panier déco', subtitle: 'Références pour vos vignettes.', limit: 12, autoplay: true, showArrows: true } },
          { id: 'hdc-h-blog', type: 'blog-grid', visible: true, props: { title: 'Carnet d’ambiances', subtitle: 'Palettes et artisans.', columns: 3, posts: [{ title: 'Mise en scène bol travertin', image: '/templates/home-decor-pinterest-v1/carre-001.png', excerpt: 'Trois vignettes.', link: '/faq' }, { title: 'Températures de lumière', image: '/templates/home-decor-pinterest-v1/carre-002.png', excerpt: '2700 K contre 3000 K.', link: '/faq' }, { title: 'Réserve accessoires', image: '/templates/home-decor-pinterest-v1/carre-003.png', excerpt: 'Comment nous photographions.', link: '/about' }] } },
          { id: 'hdc-h-bundle', type: 'bundle-offer', visible: true, props: { title: 'Capsule salon', productIds: ['hdc-prod-1', 'hdc-prod-2', 'hdc-prod-3'], discountLabel: '−12 % plateau + vase + plaid' } },
          { id: 'hdc-h-newsletter', type: 'newsletter', visible: true, props: { title: 'Lettre hebdomadaire', subtitle: 'Idées d’ambiance, uniquement le dimanche.', placeholder: 'E-mail', buttonText: 'S’abonner', backgroundColor: '#292524', backgroundImage: '', overlayOpacity: 0 } },
        ],
        shop: [
          { id: 'hdc-s-announce', type: 'announcement-bar', visible: true, props: { text: 'Fiches produit prêtes à épingler · Packs au paiement', link: '', backgroundColor: '', textColor: '' } },
          { id: 'hdc-s-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'ShieldCheck', title: 'Photos 3200 K', text: 'Couleurs fidèles pour vos tableaux.' }, { icon: 'Truck', title: 'Circuit fragile', text: 'Voie verre dédiée.' }, { icon: 'Zap', title: 'Garantie style', text: 'Échange si la finition change.' }] } },
          { id: 'hdc-s-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'hdc-s-filters', type: 'filters-sidebar', visible: true, props: { enablePrice: true, enableCategory: true, enableRating: true } },
          { id: 'hdc-s-promo-grid', type: 'promo-grid', visible: true, props: { variant: 'stacked', items: [{ title: 'Par pièce, vite', subtitle: 'Salon, chambre, table — repères pour trier sans perdre du temps.', image: '/templates/home-decor-pinterest-v1/carre-004.png', ctaLabel: 'Filtrer le catalogue', ctaLink: '/shop', size: 'large' }, { title: 'Textile & sommeil', subtitle: 'Draps, plaids, housses — toucher et entretien indiqués.', image: '/templates/home-decor-pinterest-v1/carre-005.png', ctaLabel: 'Voir textile', ctaLink: '/shop', size: 'small' }, { title: 'Luminaires & tables', subtitle: 'Suspensions, lampes, art de la table.', image: '/templates/home-decor-pinterest-v1/carre-006.png', ctaLabel: 'Voir éclairage & repas', ctaLink: '/shop', size: 'small' }] } },
          { id: 'hdc-s-gallery', type: 'image-gallery', visible: true, props: { images: ['/templates/home-decor-pinterest-v1/mood-007.svg', '/templates/home-decor-pinterest-v1/mood-008.svg', '/templates/home-decor-pinterest-v1/mood-009.svg'], layout: 'masonry', columns: 3 } },
          { id: 'hdc-s-featured', type: 'featured-collection', visible: true, props: { title: 'Céramiques et luminaires', subtitle: 'Très enregistrés sur Pinterest.', collectionId: '', limit: 8, columns: 4, viewAllLabel: 'Toute la déco', viewAllLink: '/shop' } },
          { id: 'hdc-s-grid', type: 'product-grid', visible: true, props: { title: 'Acheter depuis le moodboard', subtitle: 'Parcours dense.', limit: 48, columns: 5, anchorId: 'hdc-board' } },
          { id: 'hdc-s-newsletter', type: 'newsletter', visible: true, props: { title: 'Nouvelles ambiances pièce par pièce', subtitle: 'Pas de bruit quotidien.', placeholder: 'E-mail', buttonText: 'Recevoir l’inspiration', backgroundColor: '#44403c', backgroundImage: '', overlayOpacity: 25 } },
        ],
        product: [
          { id: 'hdc-p-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'Package', title: 'Testé en plateau', text: 'La référence correspond à la photo.' }, { icon: 'Truck', title: 'Assuré', text: 'Double carton verre.' }, { icon: 'ShieldCheck', title: '14 jours', text: 'Retours si non utilisé.' }] } },
          { id: 'hdc-p-details', type: 'product-details', visible: true, props: { highlights: ['Photo 3200 K', 'Housse poussière', 'Assortiment plateau'], trustShipping: 'Emballage fragile.', trustReturns: '14 jours si non utilisé.', trustPayment: 'Paiement à la livraison + cartes.' } },
          { id: 'hdc-p-tabs', type: 'product-tabs', visible: true, props: { tabs: [{ label: 'Dimensions', content: 'cm et pouces.' }, { label: 'Matériaux', content: 'Origine pierre et textile.' }, { label: 'Mise en scène', content: 'Associations d’accessoires.' }] } },
          { id: 'hdc-p-gallery', type: 'image-gallery', visible: true, props: { images: ['/templates/home-decor-pinterest-v1/mood-010.svg', '/templates/home-decor-pinterest-v1/carre-011.png', '/templates/home-decor-pinterest-v1/carre-012.png', '/templates/home-decor-pinterest-v1/carre-013.png'], layout: 'masonry', columns: 3 } },
          { id: 'hdc-p-shop-img', type: 'shoppable-image', visible: true, props: { image: '/templates/home-decor-pinterest-v1/carre-016.png', hotspots: [{ x: 38, y: 46, productId: 'hdc-prod-4' }, { x: 70, y: 40, productId: 'hdc-prod-5' }] } },
          { id: 'hdc-p-compare', type: 'comparison-table', visible: true, props: { title: 'Matériaux et entretien', features: ['Origine pierre', 'Surface scellée', 'Extérieur possible', 'Empilable', 'Emballage cadeau'], products: ['hdc-prod-4', 'hdc-prod-5', 'hdc-prod-6'] } },
          { id: 'hdc-p-bundle', type: 'bundle-offer', visible: true, props: { title: 'Pack vignette', productIds: ['hdc-prod-4', 'hdc-prod-5', 'hdc-prod-6'], discountLabel: '−10 % vase + bougies + plateau' } },
          { id: 'hdc-p-related', type: 'related-products', visible: true, props: { title: 'Compléter la vignette', limit: 8, columns: 4 } },
        ],
        category: [
          {
            id: 'hdc-cat-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Expédition fragile · Cartes style jointes · Retours 14 jours',
              link: '/faq',
              backgroundColor: '#292524',
              textColor: '#fafaf9',
            },
          },
          {
            id: 'hdc-cat-mood',
            type: 'image-banner',
            visible: true,
            props: {
              image: '/templates/home-decor-pinterest-v1/mood-020.svg',
              title: 'Dans cette pièce',
              subtitle: 'Textures et volumes vus sur le plateau photo.',
              ctaLabel: 'Voir le moodboard',
              ctaLink: '/shop',
              overlayOpacity: 40,
              minHeight: 'md',
              textAlign: 'left',
            },
          },
          { id: 'hdc-cat-strip', type: 'category-strip', visible: true, props: {} },
          {
            id: 'hdc-cat-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Objets complémentaires',
              subtitle: 'Pour compléter la scène',
              limit: 4,
              columns: 4,
              viewAllLabel: '',
              viewAllLink: '',
            },
          },
          { id: 'hdc-cat-grid', type: 'category-grid', visible: true, props: {} },
        ],
        cart: [
          {
            id: 'hdc-cart-lines',
            type: 'cart-lines',
            visible: true,
            props: {},
          },
          {
            id: 'hdc-cart-summary',
            type: 'cart-summary',
            visible: true,
            props: {},
          },
        ],
        checkout: [
          {
            id: 'hdc-checkout-form',
            type: 'checkout-form',
            visible: true,
            props: {},
          },
        ],
        search: [
          {
            id: 'hdc-search-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Rechercher de la déco',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#f8fafc',
            },
          },
          {
            id: 'hdc-search-results',
            type: 'search-results',
            visible: true,
            props: {},
          },
          {
            id: 'hdc-search-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Tableaux enregistrés',
              subtitle: '',
              collectionId: '',
              limit: 4,
              columns: 4,
              viewAllLabel: 'Voir tout',
              viewAllLink: '/shop',
            },
          },
        ],
        account: [
          {
            id: 'hdc-account-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Espace compte sécurisé — suivez vos commandes et vos adresses enregistrées.',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#e2e8f0',
            },
          },
        ],
        faq: [
          {
            id: 'hdc-faq-intro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Ambiances et objets fragiles : les réponses ci-dessous restent alignées sur nos emballages photo studio. Cas atypique ? Nous contacter en bas de page.',
            },
          },
          {
            id: 'hdc-faq',
            type: 'faq-accordion',
            visible: true,
            props: {
              title: 'Questions fréquentes',
              subtitle: 'Livraison',
              items: [
                {
                  question: 'Le verre ?',
                  answer: 'Double carton + assurance.',
                },
              ],
            },
          },
          {
            id: 'hdc-faq-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Parler au studio logistique',
              text: 'Nous planifions les enlèvements lourds et les créneaux — même numéro que sur le bon de livraison.',
              buttonText: 'Planifier un échange',
              buttonLink: '/contact',
            },
          },
          {
            id: 'hdc-faq-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Chez eux, chez vous',
              items: [
                {
                  name: 'Sarra',
                  role: 'Cliente',
                  text: 'Le miroir est arrivé sans une rayure — double carton tenu.',
                  rating: 5,
                },
              ],
            },
          },
          {
            id: 'hdc-faq-closing',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/home-decor-pinterest-v1/mood-030.svg',
              imageAlt: 'Ambiance intérieure',
              title: 'Inspirations hors questions fréquentes',
              text: 'Besoin d’une cote pour un meuble sur mesure ? Envoyez une photo de la pièce.',
              ctaText: 'Voir les ambiances',
              ctaLink: '/shop',
              layout: 'image-right',
              imageAspect: 'landscape',
            },
          },
        ],
        about: [
          {
            id: 'hdc-about',
            type: 'rich-text',
            visible: true,
            props: {
              title: 'Studio',
              text: 'Nous photographions comme un magazine.',
            },
          },
          {
            id: 'hdc-about-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
        ],
        contact: [
          {
            id: 'hdc-contact-hero',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/home-decor-pinterest-v1/mood-015.svg',
              imageAlt: 'Ambiance lifestyle',
              title: 'Objets & fragments — même exigence que sur le carnet',
              text: 'Colis volumineux, vitres, céramique : décrivez accès ascenseur et étage — ça évite un second passage.',
              ctaText: 'Voir livraison fragile',
              ctaLink: '/faq',
              layout: 'image-left',
              imageAspect: 'landscape',
            },
          },
          {
            id: 'hdc-contact-micro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Joignez une photo du recoin cible si vous hésitez sur la taille — nous dimensionnons avec vous.',
            },
          },
          {
            id: 'hdc-contact-info',
            type: 'contact-info',
            visible: true,
            props: {
              title: 'Studio & enlèvements',
              subtitle: 'Créneaux matin / fin de journée — créneaux serrés annoncés à l’avance.',
              phone: '+216 XX XXX XXX',
              email: 'bonjour@boutique.tn',
              workingHours: '9h–19h, lun–sam',
              address: '',
              showIcons: true,
              sectionLayout: 'grid',
            },
          },
          {
            id: 'hdc-contact-form',
            type: 'contact-form',
            visible: true,
            props: {
              title: 'Projet pièce par pièce',
              subtitle: 'Nom, e-mail et message : dimensions, code postal, contraintes d’accès.',
              showPhone: false,
              showSubject: false,
              buttonText: 'Envoyer la demande',
            },
          },
          {
            id: 'hdc-contact-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Photos sur WhatsApp',
              text: 'Envoyez la pièce vide ou le mur cible — réponse avec moodboard rapide.',
              buttonText: 'WhatsApp déco',
              buttonLink: 'https://wa.me/21600000000',
            },
          },
          {
            id: 'hdc-contact-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
          {
            id: 'hdc-contact-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Ils ont refait le salon avec nous',
              items: [
                {
                  name: 'Hela',
                  role: 'Architecte d’intérieur',
                  text: 'Réactivité sur un vase lourd — livraison planifiée à la carte.',
                  rating: 5,
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'dropship-velocity-v1',
    name: 'Boutique généraliste — volume dropship',
    description: 'Urgence et volume : compte à rebours, bandeau promo, carrousel, grille dense.',
    niche: 'Généraliste',
    thumbnail: '/templates/dropship-velocity-v1/thumb-800.webp',
    layout: {
      theme: {
        id: 'neon-glass',
        primary: 'hsl(45 93% 47%)',
        secondary: 'hsl(48 100% 96%)',
        backgroundColor: '#fffef5',
        font: 'DM Sans',
        radius: '0.5rem',
        ui: {
          productCard: 'bold',
          buttons: 'pill',
          spacing: 'compact',
          headingStyle: 'bold',
          density: 'tight',
          font: 'sans',
          mode: 'light',
          radius: 'xl',
        },
      } as any,
      header: {
        props: {
          storeName: null,
          menuItems: [{ label: 'Accueil', link: '/' }, { label: 'Boutique', link: '/shop' }, { label: 'Promos', link: '/shop' }, { label: 'Questions fréquentes', link: '/faq' }],
          showSearch: true,
          showCart: true,
          sticky: true,
          variant: 'default',
        },
      },
      footer: {
        props: {
          storeName: true,
          description: 'Références à fort débit — paiement à la livraison bienvenu.',
          copyright: `© ${new Date().getFullYear()}`,
          columns: [{ title: 'Boutique', links: [{ label: 'Catalogue', link: '/shop' }] }],
          socialLinks: { facebook: 'https://facebook.com', tiktok: 'https://tiktok.com' },
          showPaymentIcons: true,
        },
      },
      pages: {
        home: [
          { id: 'drp-h-announce', type: 'announcement-bar', visible: true, props: { text: '⚡ Offres mises à jour chaque heure · Livraison offerte dès 79 TND', link: '/shop', backgroundColor: '#fef08a', textColor: '#1e293b' } },
          {
            id: 'drp-h-hero',
            type: 'hero-carousel',
            visible: true,
            props: {
              autoplay: true,
              interval: 5500,
              showArrows: true,
              showDots: true,
              slides: [
                {
                  id: 'drp-hc-1',
                  title: 'Gros volume. Peu de friction.',
                  subtitle: 'Smartphones, soins, fitness — paiement à la livraison.',
                  image: '/templates/dropship-velocity-v1/hero-slide-1-desk-pink.png',
                  buttonText: 'Voir les best-sellers',
                  buttonLink: '#drp-deals',
                },
                {
                  id: 'drp-hc-2',
                  title: 'Les offres flash se renouvellent chaque heure',
                  subtitle: 'Cumulez les codes avant la fin du compte à rebours.',
                  image: '/templates/dropship-velocity-v1/hero-slide-2-fitness-blue.png',
                  buttonText: 'Voir les promos',
                  buttonLink: '/shop',
                },
                {
                  id: 'drp-hc-3',
                  title: 'Références contrôlées qualité',
                  subtitle: 'Litiges photo traités dans l’application.',
                  image: '/templates/dropship-velocity-v1/hero-slide-3-skincare-vanity.png',
                  buttonText: 'Lire la politique',
                  buttonLink: '/faq',
                },
              ],
            },
          },
          { id: 'drp-h-countdown', type: 'countdown-timer', visible: true, props: { title: 'Fin du méga panier', subtitle: 'Cumulez codes et cadeaux.', targetDate: '2026-11-30T23:59:00', expiredText: 'Terminé — prochain flash bientôt.', backgroundColor: '#dc2626' } },
          { id: 'drp-h-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'Truck', title: 'Expédition 24 h', text: 'Flux volume + suivi.' }, { icon: 'ShieldCheck', title: 'Protection acheteur', text: 'Litiges sur photos.' }, { icon: 'Zap', title: 'Paiement à la livraison', text: 'Zones éligibles.' }] } },
          { id: 'drp-h-promo', type: 'promo-banner', visible: true, props: { text: 'Deux best-sellers smartphones ou soins — cadeau surprise', highlight: 'Auto', backgroundColor: '#0f172a', textColor: '#f8fafc', ctaLabel: 'Profiter des offres', ctaLink: '/shop', showIcon: true } },
          { id: 'drp-h-carousel', type: 'product-carousel', visible: true, props: { title: 'Tendances du moment', subtitle: 'Faites défiler vite.', limit: 16, autoplay: true, showArrows: true, variant: 'split', sectionWidth: 'full', ctaLabel: 'Voir tout', ctaLink: '/shop' } },
          { id: 'drp-h-promo-grid', type: 'promo-grid', visible: true, props: { gridMode: 'bento', sectionWidth: 'boxed', variant: 'hero-right', items: [{ title: 'Coffre gadgets', subtitle: 'Câbles et hubs.', image: '/templates/dropship-velocity-v1/promo-gadget-coffre-tech.png', ctaLabel: 'Tech', ctaLink: '/shop', size: 'large' }, { title: 'Astuces maison', subtitle: 'Rangements.', image: '/templates/dropship-velocity-v1/promo-home-rangements.png', ctaLabel: 'Voir', ctaLink: '/shop', size: 'small' }, { title: 'Soins visage', subtitle: 'Outils viraux.', image: '/templates/dropship-velocity-v1/promo-skincare-marble.png', ctaLabel: 'Boutique', ctaLink: '/shop', size: 'small' }, { title: 'Fitness', subtitle: 'Récupération.', image: '/templates/dropship-velocity-v1/promo-fitness-recovery.png', ctaLabel: 'Bouger', ctaLink: '/shop', size: 'small' }] } },
          { id: 'drp-h-featured', type: 'featured-collection', visible: true, props: { title: 'Choix équipe — peu de retours', subtitle: 'Smartphones, soins, fitness.', collectionId: '', limit: 12, columns: 4, viewAllLabel: 'Tout le catalogue', viewAllLink: '/shop' } },
          { id: 'drp-h-shop-img', type: 'shoppable-image', visible: true, props: { image: '/templates/dropship-velocity-v1/shoppable-flatlay-gradient.png', hotspots: [{ x: 25, y: 48, productId: 'drp-prod-1' }, { x: 58, y: 42, productId: 'drp-prod-2' }, { x: 78, y: 60, productId: 'drp-prod-3' }] } },
          { id: 'drp-h-bundle', type: 'bundle-offer', visible: true, props: { title: 'Soirée soins + gadget', productIds: ['drp-prod-1', 'drp-prod-2', 'drp-prod-3'], discountLabel: '−12 % au paiement' } },
          { id: 'drp-h-grid', type: 'product-grid', visible: true, props: { title: 'Toutes les promos', subtitle: 'Filtres sur l’onglet boutique.', limit: 36, columns: 5, anchorId: 'drp-deals' } },
          { id: 'drp-h-logos', type: 'logo-carousel', visible: true, props: { logos: ['/templates/dropship-velocity-v1/vis-logo-1.svg', '/templates/dropship-velocity-v1/vis-logo-2.svg', '/templates/dropship-velocity-v1/vis-logo-3.svg', '/templates/dropship-velocity-v1/vis-logo-4.svg'], autoplay: true, duration: 24 } },
          { id: 'drp-h-faq', type: 'faq-accordion', visible: true, props: { title: 'Questions fréquentes — dropshipping', subtitle: 'Rapidité et authenticité', items: [{ question: 'Livraison rapide ?', answer: 'Routage depuis l’entrepôt le plus proche.' }, { question: 'Produits authentiques ?', answer: 'Contrats QC — contrefaçons retirées.' }] } },
          { id: 'drp-h-newsletter', type: 'newsletter', visible: true, props: { title: 'Liste SMS flash', subtitle: 'Un SMS max par jour pendant les événements.', placeholder: 'Téléphone ou e-mail', buttonText: 'Rejoindre', backgroundColor: '#111827', backgroundImage: '', overlayOpacity: 30 } },
        ],
        shop: [
          { id: 'drp-s-announce', type: 'announcement-bar', visible: true, props: { text: 'L’accueil affiche le compte à rebours — filtres et grille ici', link: '/', backgroundColor: '#0f172a', textColor: '#fef08a' } },
          { id: 'drp-s-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'Zap', title: 'Voie impulsion', text: 'Carrousel = meilleur taux d’ajout.' }, { icon: 'ShieldCheck', title: 'QC', text: 'Deux incidents = retrait.' }, { icon: 'Package', title: 'Colis fusionnés', text: 'Les packs du même nœud partent ensemble.' }] } },
          { id: 'drp-s-countdown', type: 'countdown-timer', visible: true, props: { title: 'Boost panier', subtitle: 'Cumulable avec la promo d’accueil.', targetDate: '2026-11-30T23:59:00', expiredText: 'Terminé.', backgroundColor: '#b91c1c' } },
          { id: 'drp-s-promo', type: 'promo-banner', visible: true, props: { text: '120 TND — déblocage coffre mystère', highlight: 'Limité', backgroundColor: '#111827', textColor: '#fef9c3', ctaLabel: 'Remplir le panier', ctaLink: '#drp-shop-grid', showIcon: true } },
          { id: 'drp-s-filters', type: 'filters-sidebar', visible: true, props: { enablePrice: true, enableCategory: true, enableRating: true } },
          { id: 'drp-s-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'drp-s-carousel', type: 'product-carousel', visible: true, props: { title: 'Voie impulsion — meilleurs ajouts', subtitle: 'À combiner avec les paniers.', limit: 20, autoplay: true, showArrows: false } },
          { id: 'drp-s-featured', type: 'featured-collection', visible: true, props: { title: 'Smartphones et soins qui partent vite', subtitle: 'Rotation 72 h.', collectionId: '', limit: 10, columns: 5, viewAllLabel: 'Tout', viewAllLink: '/shop' } },
          { id: 'drp-s-grid', type: 'product-grid', visible: true, props: { title: 'Vue entrepôt', subtitle: '48 par page.', limit: 48, columns: 5, anchorId: 'drp-shop-grid' } },
          { id: 'drp-s-newsletter', type: 'newsletter', visible: true, props: { title: 'Liste grossistes', subtitle: 'Nouveaux MOQ chaque mois.', placeholder: 'E-mail pro', buttonText: 'Postuler', backgroundColor: '#1e293b', backgroundImage: '', overlayOpacity: 40 } },
        ],
        product: [
          { id: 'drp-p-trust', type: 'trust-strip', visible: true, props: { items: [{ icon: 'ShieldCheck', title: 'Photo QC', text: 'Photos à la confirmation.' }, { icon: 'Truck', title: 'Transporteurs mixtes', text: 'Économique ou express.' }, { icon: 'RefreshCw', title: 'Retours 30 jours', text: 'Non utilisé — retours par QR.' }] } },
          { id: 'drp-p-promo', type: 'promo-banner', visible: true, props: { text: 'Cette référence + soin ou accessoire gadget — −8 %', highlight: 'Cumulable', backgroundColor: '#ea580c', textColor: '#fff7ed', ctaLabel: 'Parcourir', ctaLink: '/shop', showIcon: true } },
          { id: 'drp-p-details', type: 'product-details', visible: true, props: { highlights: ['Expédition photo QC', 'Pack volume', 'Notices FR/AR'], trustShipping: 'Transporteurs mixtes.', trustReturns: '30 jours si non utilisé.', trustPayment: 'Paiement à la livraison + cartes.' } },
          { id: 'drp-p-tabs', type: 'product-tabs', visible: true, props: { tabs: [{ label: 'Fournisseur', content: 'Codes lot et PDF.' }, { label: 'UGC', content: 'Clips de vente en direct.' }, { label: 'Garantie', content: 'Extension au paiement.' }] } },
          { id: 'drp-p-compare', type: 'comparison-table', visible: true, props: { title: 'Pack vs unité', features: ['Garantie', 'Pack QC', 'Paiement à la livraison', 'Express', 'Cadeau'], products: ['drp-prod-1', 'drp-prod-2', 'drp-prod-3'] } },
          { id: 'drp-p-bundle', type: 'bundle-offer', visible: true, props: { title: 'Trio fréquent', productIds: ['drp-prod-1', 'drp-prod-2', 'drp-prod-3'], discountLabel: 'Remise pack automatique' } },
          { id: 'drp-p-related', type: 'related-products', visible: true, props: { title: 'Souvent achetés ensemble', limit: 10, columns: 5 } },
        ],
        category: [
          {
            id: 'drp-cat-flash',
            type: 'promo-banner',
            visible: true,
            props: {
              text: 'Chaque catégorie passe un contrôle QC — litiges photo traités dans l’app.',
              highlight: 'Qualité',
              backgroundColor: '#ea580c',
              textColor: '#fff7ed',
              ctaLabel: 'Politique',
              ctaLink: '/faq',
              showIcon: true,
            },
          },
          {
            id: 'drp-cat-carousel',
            type: 'product-carousel',
            visible: true,
            props: {
              title: 'Top impulsion',
              subtitle: 'Ajouts rapides depuis cette catégorie.',
              limit: 12,
              autoplay: true,
              showArrows: false,
            },
          },
          { id: 'drp-cat-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'drp-cat-grid', type: 'category-grid', visible: true, props: {} },
        ],
        cart: [
          {
            id: 'drp-cart-lines',
            type: 'cart-lines',
            visible: true,
            props: {},
          },
          {
            id: 'drp-cart-summary',
            type: 'cart-summary',
            visible: true,
            props: {},
          },
        ],
        checkout: [
          {
            id: 'drp-checkout-form',
            type: 'checkout-form',
            visible: true,
            props: {},
          },
        ],
        search: [
          {
            id: 'drp-search-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Rechercher dans le catalogue',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#f8fafc',
            },
          },
          {
            id: 'drp-search-results',
            type: 'search-results',
            visible: true,
            props: {},
          },
          {
            id: 'drp-search-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Tendance actuelle',
              subtitle: '',
              collectionId: '',
              limit: 4,
              columns: 4,
              viewAllLabel: 'Voir tout',
              viewAllLink: '/shop',
            },
          },
        ],
        account: [
          {
            id: 'drp-account-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Espace compte sécurisé — suivez vos commandes et vos adresses enregistrées.',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#e2e8f0',
            },
          },
        ],
        faq: [
          {
            id: 'drp-faq-intro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Réponse PDV et wallet avant tout litige — gardez votre numéro de suivi. Sérieux sur la chaîne dropship ? Le bouton bas de page ouvre le même flux que le formulaire.',
            },
          },
          {
            id: 'drp-faq',
            type: 'faq-accordion',
            visible: true,
            props: {
              title: 'Questions fréquentes',
              subtitle: 'Paiement à la livraison et remboursements',
              items: [
                {
                  question: 'Remboursement si paiement à la livraison ?',
                  answer: 'Portefeuille ou virement 48 h après réception.',
                },
              ],
            },
          },
          {
            id: 'drp-faq-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Relancer ma commande maintenant',
              text: 'Nous contacter = même file que le formulaire — décrivez le suivi ou joignez une photo étiquette.',
              buttonText: 'Ouvrir la page contact',
              buttonLink: '/contact',
            },
          },
          {
            id: 'drp-faq-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Ils ont été remboursés vite',
              items: [
                {
                  name: 'Bilel',
                  role: 'Paiement à la livraison Tunis',
                  text: 'Statut mis à jour le même jour — pas de ticket fantôme.',
                  rating: 5,
                },
                {
                  name: 'Donia',
                  role: 'Achat impulsif',
                  text: 'J’ai eu le point relais correct en 3 messages.',
                  rating: 4,
                },
              ],
            },
          },
        ],
        about: [
          {
            id: 'drp-about',
            type: 'rich-text',
            visible: true,
            props: {
              title: 'Velocity co.',
              text: 'Nous faisons du volume avec contrôle qualité.',
            },
          },
          {
            id: 'drp-about-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
        ],
        contact: [
          {
            id: 'drp-contact-hero',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/dropship-velocity-v1/vis-slide-1.svg',
              imageAlt: 'Flux logistique rapide',
              title: 'Tracking vivant — pas de file morte',
              text: 'Ajoutez votre code commande : on ping l’entrepôt le plus proche en direct.',
              ctaText: 'Voir les remboursements (questions fréquentes)',
              ctaLink: '/faq',
              layout: 'image-right',
              imageAspect: 'landscape',
            },
          },
          {
            id: 'drp-contact-micro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Pas de roman : code + question. Nous priorisons les PDV bloqués et les retours non scannés.',
            },
          },
          {
            id: 'drp-contact-info',
            type: 'contact-info',
            visible: true,
            props: {
              title: 'Ops & litiges',
              subtitle: 'Téléphone flash entre 9h et 23h — relais WhatsApp 24/7 pour photos de colis.',
              phone: '+216 XX XXX XXX',
              email: 'support@velocity.tn',
              workingHours: '9h–23h, 7j/7',
              address: '',
              showIcons: true,
              sectionLayout: 'grid',
            },
          },
          {
            id: 'drp-contact-form',
            type: 'contact-form',
            visible: true,
            props: {
              title: 'Escalade express',
              subtitle: 'Nom, e-mail, message : une ligne par incident (code + symptôme).',
              showPhone: false,
              showSubject: false,
              buttonText: 'Envoyer — traitement express',
            },
          },
          {
            id: 'drp-contact-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Priorité route entrepôt',
              text: 'Photo étiquette + emballage = traitement dans la prochaine vague de tri.',
              buttonText: 'WhatsApp ops',
              buttonLink: 'https://wa.me/21600000000',
            },
          },
          {
            id: 'drp-contact-trust',
            type: 'trust-strip',
            visible: true,
            props: {
              items: [
                {
                  icon: 'Zap',
                  title: 'Routage hub',
                  text: 'Colis assigné à l’entrepôt le plus proche.',
                },
                {
                  icon: 'Truck',
                  title: 'Statut PDV',
                  text: 'Relais mis à jour même les jours de forte demande.',
                },
                {
                  icon: 'Package',
                  title: 'QC à l’entrée',
                  text: 'Contrôle aléatoire sur les lots sensibles.',
                },
              ],
            },
          },
          {
            id: 'drp-contact-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Preuve sociale express',
              items: [
                {
                  name: 'Wassim',
                  role: 'Revendeur',
                  text: 'Le support a récupéré mon retour en 36 h.',
                  rating: 5,
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'premium-cosmetics-v1',
    name: 'Cosmétiques — éditorial épuré',
    description:
      'Mise en page beauté premium : mouvement doux, lookbook, fiche produit centrée ingrédients, compte à rebours et narration rituelle.',
    niche: 'Beauté',
    thumbnail: '/templates/premium-cosmetics-v1/thumb.jpg',
    layout: {
      theme: {
        id: 'luxury',
        rootClassName: 'ref-premium-cosmetics-motion',
        tokens: {
          'color-primary': 'hsl(335 42% 42%)',
          'color-primary-hover': 'hsl(335 48% 36%)',
          'color-bg': '#fff8fb',
          'color-surface': '#fff0f6',
          'color-text': '#2d0a1f',
          'color-text-secondary': '#5c3d4d',
          'color-muted': '#8b6b78',
          'color-accent': 'hsl(335 55% 92%)',
          'color-accent-muted': 'rgba(214, 122, 168, 0.12)',
          'color-border': '#f3d4e4',
          'color-border-strong': '#e8b8cf',
          'color-font': "'Outfit', system-ui, sans-serif",
          'radius-sm': '0.35rem',
          'radius-md': '0.55rem',
          'radius-lg': '0.85rem',
          'radius-xl': '1.1rem',
          'shadow-soft': '0 8px 30px rgba(45, 10, 31, 0.06)',
          'shadow-card': '0 12px 40px rgba(45, 10, 31, 0.08)',
          'shadow-hover': '0 18px 50px rgba(45, 10, 31, 0.12)',
        },
        primary: 'hsl(335 42% 42%)',
        secondary: 'hsl(335 40% 96%)',
        backgroundColor: '#fff8fb',
        font: 'Outfit',
        radius: '0.85rem',
        ui: {
          productCard: 'default',
          buttons: 'primary',
          spacing: 'airy',
          headingStyle: 'editorial',
          density: 'comfortable',
          font: 'sans',
          mode: 'light',
          radius: 'md',
        },
      } as any,
      header: {
        props: {
          storeName: null,
          menuItems: [
            { label: 'Accueil', link: '/' },
            { label: 'Boutique', link: '/shop' },
            { label: 'Soins visage', link: '/category/soins-visage' },
            { label: 'Rituels', link: '/about' },
            { label: 'Nous contacter', link: '/contact' },
          ],
          showSearch: true,
          showCart: true,
          sticky: true,
          variant: 'minimal',
        },
      },
      footer: {
        props: {
          storeName: true,
          description: 'Formules propres · Petites séries · Sans cruauté depuis le premier jour.',
          copyright: `© ${new Date().getFullYear()} Maison`,
          columns: [
            { title: 'Boutique', links: [{ label: 'Tout', link: '/shop' }, { label: 'Questions fréquentes', link: '/faq' }] },
            { title: 'Infos', links: [{ label: 'Livraison', link: '/policy/shipping' }, { label: 'Retours', link: '/policy/returns' }] },
          ],
          socialLinks: { instagram: 'https://instagram.com', tiktok: 'https://tiktok.com' },
          showPaymentIcons: true,
        },
      },
      pages: {
        home: [
          {
            id: 'pcx-h-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Livraison offerte dès 120 TND · Nouveau rituel cette semaine',
              link: '/shop',
              backgroundColor: '#2d0a1f',
              textColor: '#fdeef7',
            },
          },
          {
            id: 'pcx-h-hero',
            type: 'hero',
            visible: true,
            props: {
              headline: 'Peau lumineuse, luxe discret',
              subtitle: 'Sérums, baumes et pigments — pensés pour les peaux sensibles et un éclat prêt photo.',
              ctaLabel: 'Découvrir le rituel',
              anchorId: 'pcx-grid',
              backgroundImage: '/templates/premium-cosmetics-v1/hero.jpg',
              layout: 'centered',
              overlayOpacity: 38,
              textColor: '#fff7fb',
            },
          },
          {
            id: 'pcx-h-banner',
            type: 'image-banner',
            visible: true,
            props: {
              image: '/templates/premium-cosmetics-v1/banner-ritual.jpg',
              title: 'La salle de l’éclat',
              subtitle: 'Touchez les repères — chaque flacon mène à sa fiche rituelle complète.',
              ctaLabel: 'Ouvrir le lookbook',
              ctaLink: '/shop',
              overlayOpacity: 32,
              minHeight: 'lg',
              textAlign: 'center',
            },
          },
          {
            id: 'pcx-h-shop-img',
            type: 'shoppable-image',
            visible: true,
            props: {
              image: '/templates/premium-cosmetics-v1/lookbook.jpg',
              hotspots: [
                { x: 28, y: 46, productId: 'pmc-prod-1' },
                { x: 58, y: 40, productId: 'pmc-prod-2' },
                { x: 74, y: 62, productId: 'pmc-prod-3' },
              ],
            },
          },
          {
            id: 'pcx-h-trust',
            type: 'trust-strip',
            visible: true,
            props: {
              items: [
                { icon: 'ShieldCheck', title: 'Testé dermatologiquement', text: 'Chaque lot contrôlé en stabilité.' },
                { icon: 'Truck', title: 'Expédition fraîche', text: 'Isotherme pour les références sensibles à la chaleur.' },
                { icon: 'Package', title: 'Programme recharge', text: 'Renvoyez le verre — crédit rituel.' },
              ],
            },
          },
          { id: 'pcx-h-qv', type: 'quick-view', visible: true, props: { enabled: true } },
          {
            id: 'pcx-h-promo',
            type: 'promo-banner',
            visible: true,
            props: {
              text: 'Huile visage Midnight Bloom — de retour après 6 semaines',
              highlight: 'Flacons limités',
              backgroundColor: 'hsl(335 42% 42%)',
              textColor: '#ffffff',
              ctaLabel: 'Réserver le vôtre',
              ctaLink: '/shop',
              showIcon: true,
            },
          },
          { id: 'pcx-h-spacer', type: 'spacer', visible: true, props: { size: 'md' } },
          {
            id: 'pcx-h-countdown',
            type: 'countdown-timer',
            visible: true,
            props: {
              title: 'Ouverture du coffre rituel',
              subtitle: 'Tarif avant-première pour les abonnés.',
              targetDate: '2026-12-21T21:00:00',
              expiredText: 'Coffre fermé — inscrivez-vous pour le prochain drop.',
              backgroundColor: '#2d0a1f',
            },
          },
          {
            id: 'pcx-h-promo-grid',
            type: 'promo-grid',
            visible: true,
            props: {
              items: [
                {
                  title: 'Double démaquillage',
                  subtitle: 'Huile puis gel — sans tiraillement.',
                  image: '/templates/premium-cosmetics-v1/promo-a.jpg',
                  ctaLabel: 'Explorer',
                  ctaLink: '/shop',
                  size: 'large',
                },
                {
                  title: 'Peau vitrée',
                  subtitle: 'Empilement d’essences.',
                  image: '/templates/premium-cosmetics-v1/promo-b.jpg',
                  ctaLabel: 'Boutique',
                  ctaLink: '/shop',
                  size: 'small',
                },
                {
                  title: 'Mat velouté',
                  subtitle: 'Pigments ultra légers.',
                  image: '/templates/premium-cosmetics-v1/promo-c.jpg',
                  ctaLabel: 'Voir',
                  ctaLink: '/shop',
                  size: 'small',
                },
                {
                  title: 'Soie pour le corps',
                  subtitle: 'Beurres et huiles sèches.',
                  image: '/templates/premium-cosmetics-v1/promo-d.jpg',
                  ctaLabel: 'Ressentir',
                  ctaLink: '/shop',
                  size: 'small',
                },
              ],
            },
          },
          {
            id: 'pcx-h-gallery',
            type: 'image-gallery',
            visible: true,
            props: {
              images: [
                '/templates/premium-cosmetics-v1/gal-1.jpg',
                '/templates/premium-cosmetics-v1/gal-2.jpg',
                '/templates/premium-cosmetics-v1/gal-3.jpg',
                '/templates/premium-cosmetics-v1/gal-4.jpg',
                '/templates/premium-cosmetics-v1/gal-5.jpg',
                '/templates/premium-cosmetics-v1/gal-6.jpg',
              ],
              layout: 'masonry',
              columns: 3,
            },
          },
          {
            id: 'pcx-h-image-text',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/premium-cosmetics-v1/story.jpg',
              imageAlt: 'Formulation en laboratoire',
              title: 'Formulé comme un parfum de niche',
              text: 'Nous superposons les actifs lentement — sans choc pour la barrière. Chaque pot liste l’INCI complet, la date de lot et le chimiste signataire.',
              ctaLabel: 'Lire nos exigences',
              ctaLink: '/faq',
              layout: 'image-left',
              imageAspect: 'portrait',
            },
          },
          {
            id: 'pcx-h-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Sélection peaux sensibles',
              subtitle: 'Héros peu parfumés qui restent photogéniques.',
              collectionId: '',
              limit: 8,
              columns: 4,
              viewAllLabel: 'Toute l’officine',
              viewAllLink: '/shop',
            },
          },
          {
            id: 'pcx-h-carousel',
            type: 'product-carousel',
            visible: true,
            props: {
              title: 'Favoris du vanity',
              subtitle: 'Les plus rachetés cette saison.',
              limit: 12,
              autoplay: true,
              showArrows: true,
            },
          },
          {
            id: 'pcx-h-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Vraie peau, vraie lumière',
              subtitle: 'Clientes et maquilleuses',
              items: [
                {
                  name: 'Sarra M.',
                  role: 'Acheteuse vérifiée',
                  text: 'Enfin un sérum qui calme mes rougeurs sans boulochage sous le SPF.',
                  rating: 5,
                },
                {
                  name: 'Imen K.',
                  role: 'Maquilleuse',
                  text: 'Le fini paraît luxueux à l’écran — indispensable dans mon kit.',
                  rating: 5,
                },
                {
                  name: 'Youssef B.',
                  role: 'Achat cadeau',
                  text: 'L’emballage évoquait une maison de parfum. Ma femme a adoré.',
                  rating: 5,
                },
              ],
            },
          },
          {
            id: 'pcx-h-logos',
            type: 'logo-carousel',
            visible: true,
            props: {
              logos: [
                '/templates/premium-cosmetics-v1/logo-1.jpg',
                '/templates/premium-cosmetics-v1/logo-2.jpg',
                '/templates/premium-cosmetics-v1/logo-3.jpg',
                '/templates/premium-cosmetics-v1/logo-4.jpg',
              ],
              autoplay: true,
              duration: 22,
            },
          },
          {
            id: 'pcx-h-newsletter',
            type: 'newsletter',
            visible: true,
            props: {
              title: 'Accès prioritaire aux drops',
              subtitle: 'Pas de spam quotidien — une note lumineuse quand les nouveaux lots arrivent.',
              placeholder: 'E-mail',
              buttonText: 'Rejoindre la liste',
              backgroundColor: '#2d0a1f',
              backgroundImage: '',
              overlayOpacity: 0,
            },
          },
        ],
        shop: [
          {
            id: 'pcx-s-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Les filtres respectent vos tags peau · Aperçu rapide sur chaque carte',
              link: '/faq',
              backgroundColor: '#fff0f6',
              textColor: '#2d0a1f',
            },
          },
          {
            id: 'pcx-s-trust',
            type: 'trust-strip',
            visible: true,
            props: {
              items: [
                { icon: 'ShieldCheck', title: 'Protocole patch', text: 'Données test port 48 h sur la fiche.' },
                { icon: 'Truck', title: 'Chaîne du froid', text: 'Références sensibles à la chaleur signalées.' },
                { icon: 'Zap', title: 'Concierge', text: 'Aide routine sur WhatsApp.' },
              ],
            },
          },
          { id: 'pcx-s-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'pcx-s-filters', type: 'filters-sidebar', visible: true, props: { enablePrice: true, enableCategory: true, enableRating: true } },
          {
            id: 'pcx-s-banner',
            type: 'image-banner',
            visible: true,
            props: {
              image: '/templates/premium-cosmetics-v1/shop-banner.jpg',
              title: 'Parcourir par besoin',
              subtitle: 'Barrière · éclat · maquillage apaisant',
              ctaLabel: 'Voir l’étagère',
              ctaLink: '#pcx-grid',
              overlayOpacity: 40,
              minHeight: 'md',
              textAlign: 'left',
            },
          },
          {
            id: 'pcx-s-carousel',
            type: 'product-carousel',
            visible: true,
            props: {
              title: 'Coup de cœur du linéaire',
              subtitle: 'Ce que les clientes ajoutent après la première commande.',
              limit: 14,
              autoplay: false,
              showArrows: true,
            },
          },
          {
            id: 'pcx-s-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Focus officine',
              subtitle: 'Ligne céramides — stocks bas.',
              collectionId: '',
              limit: 8,
              columns: 4,
              viewAllLabel: 'Tout voir',
              viewAllLink: '/shop',
            },
          },
          {
            id: 'pcx-s-grid',
            type: 'product-grid',
            visible: true,
            props: {
              title: 'Étagère complète',
              subtitle: 'Affinez par besoin, fini ou prix.',
              limit: 36,
              columns: 4,
              anchorId: 'pcx-grid',
            },
          },
          {
            id: 'pcx-s-newsletter',
            type: 'newsletter',
            visible: true,
            props: {
              title: 'Chuchotement de réassort',
              subtitle: 'Uniquement votre teinte.',
              placeholder: 'E-mail',
              buttonText: 'Me prévenir',
              backgroundColor: 'hsl(335 42% 42%)',
              backgroundImage: '',
              overlayOpacity: 15,
            },
          },
        ],
        product: [
          {
            id: 'pcx-p-trust',
            type: 'trust-strip',
            visible: true,
            props: {
              items: [
                { icon: 'ShieldCheck', title: 'Lot tracé', text: 'Scannez le QR sous le flacon.' },
                { icon: 'Truck', title: 'Expédition assurée', text: 'Verre et pompes protégés.' },
                { icon: 'RefreshCw', title: 'Essai 14 jours', text: 'Retours si non ouvert.' },
              ],
            },
          },
          {
            id: 'pcx-p-details',
            type: 'product-details',
            visible: true,
            props: {
              highlights: ['Complexe niacinamide 5 %', 'Pompe airless', 'Parfum en option'],
              trustShipping: 'Suivi + pochette fraîche si besoin.',
              trustReturns: '14 jours sur unités scellées.',
              trustPayment: 'Paiement à la livraison + cartes — total confirmé avant débit.',
            },
          },
          {
            id: 'pcx-p-tabs',
            type: 'product-tabs',
            visible: true,
            props: {
              tabs: [
                {
                  label: 'Ingrédients',
                  content:
                    'INCI complet sur l’étui. Points forts : niacinamide, panthénol, squale, tocophérol. Sans parabènes ni colorants de synthèse sur cette référence.',
                },
                { label: 'Mode d’emploi', content: 'Matin et soir après démaquillage. Tapotez — sans frotter — jusqu’à pénétration. SPF le matin.' },
                { label: 'Livraison et conservation', content: 'Tenir debout, à l’abri du soleil. Réfrigération possible pour plus d’apaisement.' },
              ],
            },
          },
          {
            id: 'pcx-p-shop-img',
            type: 'shoppable-image',
            visible: true,
            props: {
              image: '/templates/premium-cosmetics-v1/pdp-scene.jpg',
              hotspots: [
                { x: 34, y: 50, productId: 'pmc-prod-1' },
                { x: 66, y: 44, productId: 'pmc-prod-2' },
              ],
            },
          },
          {
            id: 'pcx-p-compare',
            type: 'comparison-table',
            visible: true,
            props: {
              title: 'Comparer les formules éclat',
              features: ['% niacinamide', 'Pompe airless', 'Sans parfum', 'Vegan', 'Recharge'],
              products: ['pmc-prod-1', 'pmc-prod-2', 'pmc-prod-3'],
            },
          },
          {
            id: 'pcx-p-bundle',
            type: 'bundle-offer',
            visible: true,
            props: {
              title: 'Rituel complet',
              productIds: ['pmc-prod-1', 'pmc-prod-2', 'pmc-prod-3'],
              discountLabel: '−12 % sur le trio',
            },
          },
          {
            id: 'pcx-p-related',
            type: 'related-products',
            visible: true,
            props: { title: 'S’associe parfaitement avec', limit: 8, columns: 4 },
          },
          {
            id: 'pcx-p-sticky',
            type: 'sticky-cta',
            visible: true,
            props: {
              text: 'Besoin d’aide pour choisir une routine ?',
              buttonLabel: 'Nous écrire',
              link: '/contact',
              visibleOn: 'mobile',
            },
          },
        ],
        category: [
          {
            id: 'pcx-cat-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Chaîne du froid pour références sensibles à la chaleur',
              link: '/shop',
              backgroundColor: '#2d0a1f',
              textColor: '#fdeef7',
            },
          },
          {
            id: 'pcx-cat-ritual',
            type: 'rich-text',
            visible: true,
            props: {
              title: 'Routine en 3 gestes',
              text: 'Nettoyer · traiter · hydrater — le même ordre que sur nos fiches rituel. Les références sensibles partent en colis isotherme quand nécessaire.',
            },
          },
          { id: 'pcx-cat-strip', type: 'category-strip', visible: true, props: {} },
          { id: 'pcx-cat-grid', type: 'category-grid', visible: true, props: {} },
        ],
        cart: [
          {
            id: 'pcx-cart-lines',
            type: 'cart-lines',
            visible: true,
            props: {},
          },
          {
            id: 'pcx-cart-summary',
            type: 'cart-summary',
            visible: true,
            props: {},
          },
        ],
        checkout: [
          {
            id: 'pcx-checkout-form',
            type: 'checkout-form',
            visible: true,
            props: {},
          },
        ],
        search: [
          {
            id: 'pcx-search-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Rechercher dans l’officine',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#f8fafc',
            },
          },
          {
            id: 'pcx-search-results',
            type: 'search-results',
            visible: true,
            props: {},
          },
          {
            id: 'pcx-search-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Sérums tendance',
              subtitle: '',
              collectionId: '',
              limit: 4,
              columns: 4,
              viewAllLabel: 'Voir tout',
              viewAllLink: '/shop',
            },
          },
        ],
        account: [
          {
            id: 'pcx-account-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Espace compte sécurisé — suivez vos commandes et vos adresses enregistrées.',
              link: '',
              backgroundColor: '#0f172a',
              textColor: '#e2e8f0',
            },
          },
        ],
        faq: [
          {
            id: 'pcx-faq-intro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Les réponses ci-dessous reprennent nos engagements étiquette — la même sobrieté que sur la ligne de flacons. Pour une Routine sur mesure, un conseiller vous lit en bas de page.',
            },
          },
          {
            id: 'pcx-faq',
            type: 'faq-accordion',
            visible: true,
            props: {
              title: 'Questions fréquentes — beauté',
              subtitle: 'Peau et livraison',
              items: [
                {
                  question: 'Testez-vous sur les animaux ?',
                  answer: 'Jamais. Alignement Leaping Bunny et déclarations fournisseurs publiées.',
                },
                {
                  question: 'Puis-je utiliser des acides enceinte ?',
                  answer: 'Demandez toujours à votre médecin. Nous signalons les précautions grossesse sur chaque fiche.',
                },
              ],
            },
          },
          {
            id: 'pcx-faq-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Confier sa peau à un humain',
              text: 'Message ou WhatsApp — même tonalité que l’officine, jamais de script.',
              buttonText: 'Écrire au conseiller beauté',
              buttonLink: '/contact',
            },
          },
          {
            id: 'pcx-faq-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Peaux qui ont franchi le doute',
              items: [
                {
                  name: 'Imen',
                  role: 'Peau sensible',
                  text: 'Réponse sur les acides sans jargon inutile.',
                  rating: 5,
                },
              ],
            },
          },
          {
            id: 'pcx-faq-closing',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/premium-cosmetics-v1/lookbook.jpg',
              imageAlt: 'Rituel beauté',
              title: 'Quand les questions fréquentes ne suffisent pas',
              text: 'Nous recueillons votre routine actuelle avant de suggérer un geste — toujours en petites touches.',
              ctaText: 'Voir le rituel',
              ctaLink: '/shop',
              layout: 'image-left',
              imageAspect: 'portrait',
            },
          },
        ],
        about: [
          {
            id: 'pcx-about',
            type: 'rich-text',
            visible: true,
            props: {
              title: 'Notre maison',
              text: 'Née à Tunis d’un amour du luxe discret et des étiquettes honnêtes. Petites cuves pour que chaque flacon reste frais.',
            },
          },
          {
            id: 'pcx-about-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
        ],
        contact: [
          {
            id: 'pcx-contact-hero',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/premium-cosmetics-v1/banner-ritual.jpg',
              imageAlt: 'Rituel premium',
              title: 'Un message — une voix d’officine',
              text: 'Ingrédients, incompatibilités, chaîne du froid : nous répondons avec la même exigence que sur vos fiches INCI.',
              ctaText: 'Lire la charte peau',
              ctaLink: '/faq',
              layout: 'image-right',
              imageAspect: 'portrait',
            },
          },
          {
            id: 'pcx-contact-micro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Deux lignes suffisent — ce qui vous préoccupe aujourd’hui, ce que vous utilisez déjà.',
            },
          },
          {
            id: 'pcx-contact-info',
            type: 'contact-info',
            visible: true,
            props: {
              title: 'Officine & horaires',
              subtitle: 'Réponses calmes, sans sur-promesse — alignées sur nos cuves limitées.',
              phone: '+216 XX XXX XXX',
              email: 'bonjour@boutique.tn',
              workingHours: '10h–20h, lun–dim',
              address: '',
              showIcons: true,
              sectionLayout: 'grid',
            },
          },
          {
            id: 'pcx-contact-form',
            type: 'contact-form',
            visible: true,
            props: {
              title: 'Demande personnalisée',
              subtitle: 'Nom, e-mail et message : décrivez votre peau en trois mots si vous le souhaitez.',
              showPhone: false,
              showSubject: false,
              buttonText: 'Envoyer avec élégance',
            },
          },
          {
            id: 'pcx-contact-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'WhatsApp privé',
              text: 'Selfie lumière naturelle accepté pour un avis teinte — uniquement si vous l’offrez.',
              buttonText: 'WhatsApp beauté',
              buttonLink: 'https://wa.me/21600000000',
            },
          },
          {
            id: 'pcx-contact-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
          {
            id: 'pcx-contact-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Elles ont écrit avant la première goutte',
              items: [
                {
                  name: 'Salma',
                  role: 'Cliente maison',
                  text: 'Une réponse sur la compatibilité niacinamide en un échange.',
                  rating: 5,
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'premium-sleek-editorial-v1',
    name: 'Éditorial — accueil premium épuré',
    description:
      'Vitrine épurée avec visuels propres au template — silhouettes studio, shoppable et déclinaisons lookbook isolées.',
    niche: 'Mode',
    thumbnail: '/templates/premium-sleek-editorial-v1/sleek-thumb.webp',
    layout: {
      theme: {
        id: 'minimal-white',
        primary: 'hsl(215 26% 38%)',
        secondary: 'hsl(38 28% 96%)',
        backgroundColor: '#faf8f5',
        font: 'DM Sans',
        radius: '0.75rem',
        ui: {
          productCard: 'luxury',
          buttons: 'sharp',
          spacing: 'airy',
          headingStyle: 'clean',
          density: 'comfortable',
          font: 'sans',
          mode: 'light',
          radius: 'none',
        },
      } as any,
      header: {
        props: {
          storeName: null,
          menuItems: [
            { label: 'Accueil', link: '/' },
            { label: 'Boutique', link: '/shop' },
            { label: 'Prêt-à-porter', link: '/category/apparel' },
            { label: 'Accessoires', link: '/category/accessories' },
            { label: 'Questions fréquentes', link: '/faq' },
            { label: 'Nous contacter', link: '/contact' },
          ],
          showSearch: true,
          showCart: true,
          sticky: true,
          variant: 'minimal',
        },
      },
      footer: {
        props: {
          storeName: true,
          description: 'Luxe discret, rythme éditorial — petites séries, savoir-faire transparent.',
          copyright: `© ${new Date().getFullYear()} Boutique`,
          columns: [
            { title: 'Boutique', links: [{ label: 'Tous les produits', link: '/shop' }, { label: 'Lookbook', link: '/shop' }] },
            { title: 'Assistance', links: [{ label: 'Livraison', link: '/faq' }, { label: 'Retours', link: '/faq' }] },
          ],
          socialLinks: { instagram: 'https://instagram.com', facebook: 'https://facebook.com' },
          showPaymentIcons: true,
        },
      },
      pages: {
        home: [
          {
            id: 'slk-h-hero-carousel',
            type: 'hero-carousel',
            visible: true,
            props: {
              autoplay: true,
              interval: 5200,
              showArrows: true,
              showDots: true,
              slides: [
                {
                  id: 'slk-hc-s2',
                  title: 'La capsule atelier',
                  subtitle: 'Détails finis à la main — ressentis avant d’être vus.',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-01.jpg',
                  buttonText: 'Voir la capsule',
                  buttonLink: '/category/apparel',
                },
                {
                  id: 'slk-hc-s3',
                  title: 'Pensé pour se répéter au dressing',
                  subtitle: 'Moins de pièces, plus de rotation — pour de vraies garde-robes.',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-02.jpg',
                  buttonText: 'Explorer les best-sellers',
                  buttonLink: '/product/silhouette-manteau-01',
                },
              ],
            },
          },
          {
            id: 'slk-h-pg-bento',
            type: 'promo-grid',
            visible: true,
            props: {
              items: [
                {
                  title: 'La sélection signature',
                  subtitle: 'Notre silhouette phare — tombé sur mesure, main lourde.',
                  image: '/templates/premium-sleek-editorial-v1/selection-signature-flatlay.png',
                  ctaLabel: 'Voir la sélection',
                  ctaLink: '/product/silhouette-manteau-01',
                  size: 'large',
                },
                {
                  title: 'Twill de soie',
                  subtitle: 'Fini lumineux, du jour au soir.',
                  image: '/templates/premium-sleek-editorial-v1/twill-de-soie-foulard.png',
                  ctaLabel: 'Voir les foulards',
                  ctaLink: '/category/accessories',
                  size: 'small',
                },
                {
                  title: 'Cuir monochrome',
                  subtitle: 'Quincaillerie sculptée, angles adoucis.',
                  image: '/templates/premium-sleek-editorial-v1/cuir-monochrome-sac.png',
                  ctaLabel: 'Voir les sacs',
                  ctaLink: '/category/leather-goods',
                  size: 'small',
                },
              ],
            },
          },
          { id: 'slk-h-catstrip', type: 'category-strip', visible: true, props: {} },
          {
            id: 'slk-h-pc-restock',
            type: 'product-carousel',
            visible: true,
            props: {
              title: 'De retour en stock',
              subtitle: 'Basiques réassortis qui partent à chaque drop.',
              limit: 10,
              autoplay: true,
              showArrows: true,
            },
          },
          {
            id: 'slk-h-life-banner',
            type: 'image-banner',
            visible: true,
            props: {
              image: '/templates/premium-sleek-editorial-v1/vis-slide-06.svg',
              title: 'Poli mais vécu',
              subtitle: 'Du trajet matinal aux soirées tardives — un seul rythme de garde-robe.',
              ctaLabel: 'Voir le lookbook',
              ctaLink: '/shop',
              overlayOpacity: 48,
              minHeight: 'lg',
              textAlign: 'center',
            },
          },
          {
            id: 'slk-h-pg-trio',
            type: 'promo-grid',
            visible: true,
            props: {
              items: [
                {
                  title: 'Bleu nuit',
                  subtitle: 'Profondeur sans lourdeur.',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-01.jpg',
                  ctaLabel: 'Voir le bleu',
                  ctaLink: '/category/apparel',
                  size: 'small',
                },
                {
                  title: 'Tailleur ivoire',
                  subtitle: 'Lignes nettes, épaules adoucies.',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-02.jpg',
                  ctaLabel: 'Voir le tailleur',
                  ctaLink: '/product/blazer-structure-studio',
                  size: 'small',
                },
                {
                  title: 'Accessoires graphite',
                  subtitle: 'Métaux micro-texturés.',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-03.svg',
                  ctaLabel: 'Parcourir les accessoires',
                  ctaLink: '/category/accessories',
                  size: 'small',
                },
              ],
            },
          },
          {
            id: 'slk-h-pc-bestsellers',
            type: 'product-carousel',
            visible: true,
            props: {
              title: 'Les plus vendus ce mois-ci',
              subtitle: 'Références à forte vélocité et taux de rachat élevé.',
              limit: 14,
              autoplay: false,
              showArrows: true,
            },
          },
          {
            id: 'slk-h-pg-micro',
            type: 'promo-grid',
            visible: true,
            props: {
              items: [
                {
                  title: 'Concierge 48 h',
                  subtitle: 'Mise de côté prioritaire des tailles.',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-04.svg',
                  ctaLabel: 'Réserver un créneau',
                  ctaLink: '/contact',
                  size: 'small',
                },
                {
                  title: 'Ourlet offert',
                  subtitle: 'Finitions en atelier.',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-05.svg',
                  ctaLabel: 'En savoir plus',
                  ctaLink: '/faq',
                  size: 'small',
                },
                {
                  title: 'Livraison neutre en carbone',
                  subtitle: 'Compensation sur chaque colis.',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-06.svg',
                  ctaLabel: 'Notre politique',
                  ctaLink: '/about',
                  size: 'small',
                },
                {
                  title: 'Vente archives',
                  subtitle: 'Dernières unités — derniers prix.',
                  image: '/templates/premium-sleek-editorial-v1/vis-sleek-hero.svg',
                  ctaLabel: 'Voir les archives',
                  ctaLink: '/shop',
                  size: 'small',
                },
              ],
            },
          },
          {
            id: 'slk-h-iwt',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/premium-sleek-editorial-v1/vis-sleek-shoppable.svg',
              imageAlt: 'Savoir-faire d’atelier',
              title: 'Fabriqué là où la patience compte encore',
              text: 'Petites séries, filatures auditées et contrôle tactile à chaque étape. Nous sortons moins de références pour que chacune mérite sa place dans votre rotation.',
              ctaLabel: 'Lire nos exigences',
              ctaLink: '/about',
              layout: 'image-left',
              imageAspect: 'portrait',
            },
          },
          {
            id: 'slk-h-blog',
            type: 'blog-grid',
            visible: true,
            props: {
              title: 'Journal',
              subtitle: 'Systèmes de style, rituels d’entretien, guides de ville.',
              columns: 3,
              posts: [
                {
                  title: 'La formule trois pièces pour les trajets',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-01.jpg',
                  excerpt:
                    'Comment composer une capsule qui tient réunions, métro et soirée sans changer de tenue.',
                  link: '/shop',
                },
                {
                  title: 'Entretien des pièces d’investissement',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-02.jpg',
                  excerpt: 'Mythes du lavage à froid, vapeur ou fer, et quand faire confiance à l’atelier plutôt qu’à votre fer maison.',
                  link: '/faq',
                },
                {
                  title: 'Carnet d’approvisionnement à Paris',
                  image: '/templates/premium-sleek-editorial-v1/vis-slide-03.svg',
                  excerpt: 'Notes de notre dernier voyage — filatures, accessoires et textures ramenées.',
                  link: '/about',
                },
              ],
            },
          },
          {
            id: 'slk-h-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Voix de la cabine d’essayage',
              subtitle: 'Clientes vérifiées sur drapé, finition et service.',
              items: [
                {
                  name: 'Nour H.',
                  role: 'Acheteuse vérifiée · Tunis',
                  text: 'Le manteau paraît luxueux en vrai — poids, doublure et mouvement exactement ce que je voulais.',
                  rating: 5,
                  avatar: '/templates/premium-sleek-editorial-v1/vis-slide-04.svg',
                },
                {
                  name: 'Karim L.',
                  role: 'Client fidèle',
                  text: 'Livraison rapide, emballage premium, et les conseils taille sur WhatsApp étaient justes.',
                  rating: 5,
                  avatar: '/templates/premium-sleek-editorial-v1/vis-slide-05.svg',
                },
                {
                  name: 'Yasmine A.',
                  role: 'Premier achat',
                  text: 'J’hésitais à commander du sur-mesure en ligne — la vidéo de patronage m’a convaincue. Zéro retouche.',
                  rating: 5,
                  avatar: '/templates/premium-sleek-editorial-v1/vis-slide-06.svg',
                },
              ],
            },
          },
          {
            id: 'slk-h-logos',
            type: 'logo-carousel',
            visible: true,
            props: {
              logos: [
                '/templates/premium-sleek-editorial-v1/vis-slide-01.jpg',
                '/templates/premium-sleek-editorial-v1/vis-slide-02.jpg',
                '/templates/premium-sleek-editorial-v1/vis-slide-03.svg',
                '/templates/premium-sleek-editorial-v1/vis-slide-04.svg',
                '/templates/premium-sleek-editorial-v1/vis-slide-05.svg',
                '/templates/premium-sleek-editorial-v1/vis-slide-06.svg',
              ],
              autoplay: true,
              duration: 24,
            },
          },
          {
            id: 'slk-h-pc-curated',
            type: 'product-carousel',
            visible: true,
            props: {
              title: 'Sélectionné pour vous',
              subtitle: 'Nouveautés alignées sur votre navigation — mises à jour à chaque arrivage.',
              limit: 8,
              autoplay: true,
              showArrows: false,
            },
          },
          {
            id: 'slk-h-newsletter',
            type: 'newsletter',
            visible: true,
            props: {
              title: 'Drops privés, zéro bruit',
              subtitle: 'Accès avant-première aux capsules, réassorts et rendez-vous atelier — au plus un e-mail par semaine.',
              placeholder: 'Adresse e-mail',
              buttonText: 'Demander l’accès',
              backgroundColor: '#e8e4dc',
              backgroundImage: '',
              overlayOpacity: 0,
            },
          },
        ],
        shop: [
          {
            id: 'slk-s-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Ourlet offert dès 250 TND · Rendez-vous atelier ven–dim',
              link: '/shop',
              backgroundColor: '#fafaf9',
              textColor: '#1c1917',
            },
          },
          {
            id: 'slk-s-trust',
            type: 'trust-strip',
            visible: true,
            props: {
              items: [
                { icon: 'Truck', title: 'Livraison suivie', text: 'Remise assurée sur tout le territoire.' },
                { icon: 'ShieldCheck', title: 'Tissus authentiques', text: 'Certificats filature sur demande.' },
                { icon: 'RefreshCw', title: 'Retours 14 jours', text: 'Non porté avec étiquettes.' },
              ],
            },
          },
          { id: 'slk-s-strip', type: 'category-strip', visible: true, props: {} },
          {
            id: 'slk-s-filters',
            type: 'filters-sidebar',
            visible: true,
            props: { enablePrice: true, enableCategory: true, enableRating: true },
          },
          {
            id: 'slk-s-banner',
            type: 'image-banner',
            visible: true,
            props: {
              image: '/templates/premium-sleek-editorial-v1/vis-sleek-shoppable.svg',
              title: 'Parcourir le showroom',
              subtitle: 'Notes éditoriales sur chaque fiche produit.',
              ctaLabel: 'Voir le catalogue',
              ctaLink: '#slk-shop-grid',
              overlayOpacity: 42,
              minHeight: 'md',
              textAlign: 'left',
            },
          },
          {
            id: 'slk-s-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Sélection équipe — meilleur taux de mise au panier',
              subtitle: 'Silhouettes les plus rachetées.',
              collectionId: '',
              limit: 8,
              columns: 4,
              viewAllLabel: 'Voir tout',
              viewAllLink: '/shop',
            },
          },
          {
            id: 'slk-s-carousel',
            type: 'product-carousel',
            visible: true,
            props: {
              title: 'Nouveautés de la semaine',
              subtitle: 'Quantités limitées par taille.',
              limit: 12,
              autoplay: true,
              showArrows: true,
            },
          },
          {
            id: 'slk-s-grid',
            type: 'product-grid',
            visible: true,
            props: { title: 'Catalogue complet', subtitle: 'Filtrez puis ouvrez l’aperçu rapide.', limit: 36, columns: 4, anchorId: 'slk-shop-grid' },
          },
          {
            id: 'slk-s-newsletter',
            type: 'newsletter',
            visible: true,
            props: {
              title: 'Réassort par taille',
              subtitle: 'Nous écrivons seulement quand votre taille revient.',
              placeholder: 'E-mail',
              buttonText: 'S’inscrire',
              backgroundColor: '#ddd6cb',
              backgroundImage: '',
              overlayOpacity: 0,
            },
          },
        ],
        product: [
          {
            id: 'slk-p-trust',
            type: 'trust-strip',
            visible: true,
            props: {
              items: [
                { icon: 'ShieldCheck', title: 'Contrôle qualité', text: 'Inspection manuelle avant envoi.' },
                { icon: 'Truck', title: 'Colis suivi', text: 'SMS à la création de l’étiquette.' },
                { icon: 'Package', title: 'Boîte cadeau', text: 'Ruban sur demande.' },
              ],
            },
          },
          {
            id: 'slk-p-details',
            type: 'product-details',
            visible: true,
            props: {
              highlights: ['Doublé pour la tenue', 'Boutons corozo', 'Carte d’entretien incluse'],
              trustShipping: 'Emballage premium sur demande.',
              trustReturns: 'Retours 14 jours si non porté.',
              trustPayment: 'Paiement à la livraison + cartes — total confirmé avant débit.',
            },
          },
          {
            id: 'slk-p-tabs',
            type: 'product-tabs',
            visible: true,
            props: {
              tabs: [
                { label: 'Coupe et tissu', content: 'Grammage, mensurations du mannequin et notes d’élasticité.' },
                { label: 'Direction artistique', content: 'Associations des shootings studio.' },
                { label: 'Entretien', content: 'Lavage à froid sur l’envers — séchage à plat.' },
              ],
            },
          },
          {
            id: 'slk-p-bundle',
            type: 'bundle-offer',
            visible: true,
            props: { title: 'Compléter le look', productIds: ['esk-prod-1', 'esk-prod-2', 'esk-prod-3'], discountLabel: '−10 % sur l’ensemble trois pièces' },
          },
          { id: 'slk-p-related', type: 'related-products', visible: true, props: { title: 'Vous aimerez aussi', limit: 8, columns: 4 } },
        ],
        category: [
          {
            id: 'slk-cat-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Tissus authentiques · Retours 14 jours · Ourlet offert dès 250 TND',
              link: '/faq',
              backgroundColor: '#f5f5f4',
              textColor: '#44403c',
            },
          },
          {
            id: 'slk-cat-banner',
            type: 'image-banner',
            visible: true,
            props: {
              image: '/templates/premium-sleek-editorial-v1/vis-sleek-shoppable.svg',
              title: 'Silhouettes de cette gamme',
              subtitle: 'Plan serré sur textures et tombés.',
              ctaLabel: 'Voir la boutique',
              ctaLink: '/shop',
              overlayOpacity: 38,
              minHeight: 'md',
              textAlign: 'center',
            },
          },
          {
            id: 'slk-cat-filters',
            type: 'filters-sidebar',
            visible: true,
            props: { enablePrice: true, enableCategory: true, enableRating: true },
          },
          { id: 'slk-cat-grid', type: 'category-grid', visible: true, props: {} },
        ],
        cart: [
          {
            id: 'slk-cart-lines',
            type: 'cart-lines',
            visible: true,
            props: {},
          },
          {
            id: 'slk-cart-summary',
            type: 'cart-summary',
            visible: true,
            props: {},
          },
        ],
        checkout: [
          {
            id: 'slk-checkout-form',
            type: 'checkout-form',
            visible: true,
            props: {},
          },
        ],
        search: [
          {
            id: 'slk-search-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Rechercher dans la collection',
              link: '',
              backgroundColor: '#f5f5f4',
              textColor: '#44403c',
            },
          },
          { id: 'slk-search-results', type: 'search-results', visible: true, props: {} },
          {
            id: 'slk-search-featured',
            type: 'featured-collection',
            visible: true,
            props: {
              title: 'Tendances du moment',
              subtitle: '',
              collectionId: '',
              limit: 4,
              columns: 4,
              viewAllLabel: 'Voir tout',
              viewAllLink: '/shop',
            },
          },
        ],
        account: [
          {
            id: 'slk-account-announce',
            type: 'announcement-bar',
            visible: true,
            props: {
              text: 'Espace compte sécurisé — suivez vos commandes et vos adresses enregistrées.',
              link: '',
              backgroundColor: '#f5f5f4',
              textColor: '#44403c',
            },
          },
        ],
        faq: [
          {
            id: 'slk-faq-intro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Réponses brèves — espace respirant autour des mots. Export ou entretien atelier : même ligne directe qu’en bas de page.',
            },
          },
          {
            id: 'slk-faq',
            type: 'faq-accordion',
            visible: true,
            props: {
              title: 'Questions fréquentes',
              subtitle: 'Livraison et entretien',
              items: [
                {
                  question: 'Livraison internationale ?',
                  answer: 'Nous livrons d’abord partout en Tunisie ; message privé pour devis export.',
                },
              ],
            },
          },
          {
            id: 'slk-faq-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Écrire au studio',
              text: 'Pas de centre d’appel — un message unique suffit.',
              buttonText: 'Vers Nous contacter',
              buttonLink: '/contact',
            },
          },
          {
            id: 'slk-faq-testimonials',
            type: 'testimonials',
            visible: true,
            props: {
              title: 'Fidèles aux petites séries',
              items: [
                {
                  name: 'Aya',
                  role: 'Cliente',
                  text: 'Réponse sobre sur le retour — comme la ligne du site.',
                  rating: 5,
                },
              ],
            },
          },
          {
            id: 'slk-faq-closing',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/premium-sleek-editorial-v1/vis-slide-04.svg',
              imageAlt: 'Silhouette éditoriale',
              title: 'Au-delà des questions fréquentes',
              text: 'Pour les tailles limitées et les retours atelier, nous lisons chaque message à la main.',
              ctaText: 'Voir la capsule',
              ctaLink: '/shop',
              layout: 'image-left',
              imageAspect: 'square',
            },
          },
        ],
        about: [
          {
            id: 'slk-about',
            type: 'rich-text',
            visible: true,
            props: {
              title: 'Notre studio',
              text: 'Mode éditoriale depuis Tunis — petites séries, étiquettes honnêtes.',
            },
          },
          {
            id: 'slk-about-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
        ],
        contact: [
          {
            id: 'slk-contact-hero',
            type: 'image-with-text',
            visible: true,
            props: {
              image: '/templates/premium-sleek-editorial-v1/vis-slide-03.svg',
              imageAlt: 'Éditorial sleek',
              title: 'Nous contacter — comme une légende de lookbook',
              text: 'Prix, retours atelier, délais capsule : posez une seule question claire — nous répondons sans sur-articuler.',
              ctaText: 'Lire les questions fréquentes',
              ctaLink: '/faq',
              layout: 'image-right',
              imageAspect: 'square',
            },
          },
          {
            id: 'slk-contact-micro',
            type: 'rich-text',
            visible: true,
            props: {
              text: 'Adressez-vous comme au studio — nous éviterons les réponses préfabriquées.',
            },
          },
          {
            id: 'slk-contact-info',
            type: 'contact-info',
            visible: true,
            props: {
              title: 'Studio, pas un centre',
              subtitle: 'Boîtes lues le matin et le soir — pas d’instantané artificiel.',
              phone: '+216 XX XXX XXX',
              email: 'studio@boutique.tn',
              workingHours: 'Sur rendez-vous numérique',
              address: '',
              showIcons: true,
              sectionLayout: 'grid',
            },
          },
          {
            id: 'slk-contact-form',
            type: 'contact-form',
            visible: true,
            props: {
              title: 'Ligne directe',
              subtitle: 'Nom, e-mail et message — trois phrases maximum suffisent.',
              showPhone: false,
              showSubject: false,
              buttonText: 'Envoyer',
            },
          },
          {
            id: 'slk-contact-support',
            type: 'support-cta',
            visible: true,
            props: {
              title: 'Silence utile',
              text: 'WhatsApp pour la photo d’une couture ou d’une étiquette — réponse après passage en studio.',
              buttonText: 'WhatsApp studio',
              buttonLink: 'https://wa.me/21600000000',
            },
          },
          {
            id: 'slk-contact-trust',
            type: 'trust-strip',
            visible: true,
            props: {},
          },
        ],
      },
    },
  },
]
